import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { statusLabel } from "../../localization/zh-CN";
import { CoreApiError } from "../../core-api/api-client";
import { describeActionError } from "../../core-api/errors";
import { SenderSuppressionPanel } from "./SenderSuppressionPanel";
import {
  workforceApi,
  type WorkforceMembership,
  type WorkforceTeam,
} from "../workforce/api";
import {
  decideTriage,
  loadTriageInquiries,
  loadTriageScopes,
  loadTriageBusinessWorkbench,
  loadRealShadowInquiries,
  ensurePendingRealShadowAgents,
  distributeRealShadowInquiry,
  distributeRealShadowGroup,
  createRealShadowGasPrecheck,
  mergeRealShadowInquiries,
  dismissRealShadowMergeCandidate,
  mergeFormalInquiries,
  dismissFormalInquiryMergeCandidate,
  distributionResultLabel,
  processingResultOf,
  runRealShadowAgent,
  loadOriginalMailMessage,
  saveRealShadowProductFields,
  saveRealShadowCustomerProfile,
  saveTriageProductFields,
  saveTriageCustomerProfile,
  clearTriageDomainRisk,
  triageAgentProfileOf,
  type TriageCategory,
  type TriageExceptionType,
  type TriageInquiry,
  type RealShadowInquiry,
  type TriageBusinessWorkbenchDetail,
  type RealShadowDistributionResult,
  type RealShadowGroupDistributionResult,
  type FormalInquiryDistributionResult,
  type HumanGmailSourcingStrategy,
  type RealShadowMergeResult,
  type TriageWorkbenchSubjectRef,
} from "./api";
import {
  TriageBusinessWorkbench,
  handledElsewhereReasons,
  type ConfirmedInquiryFields,
  type CustomerProfileDraft,
  type HandledElsewhereReason,
} from "./TriageBusinessWorkbench";
import { TriageWorkbenchShell } from "./TriageWorkbenchShell";
import "./triage.css";

function isSyntheticFixtureRealItem(item: RealShadowInquiry): boolean {
  return (
    item.triageDecisionProfile?.source === "SYNTHETIC_FIXTURE" ||
    /^Synthetic(?:\s|$)/i.test(
      item.customerBusinessProfile.organizationName ?? "",
    ) ||
    /^Synthetic(?:\s|$)/i.test(item.inquiryDetailProfile.productName ?? "") ||
    /(?:SYNTHETIC|DO NOT CONTACT|EVITACHEM UAT)/i.test(
      item.inquiryDetailProfile.projectDetails ?? "",
    )
  );
}

function isSyntheticFixtureQueueItem(item: TriageInquiry): boolean {
  return (
    /(?:Synthetic|EVITACHEM UAT|DO NOT CONTACT)/i.test(
      `${item.customerCandidate ?? ""} ${item.product ?? ""} ${item.inquiryNumber ?? ""}`,
    ) || item.triageDecisionProfile?.source === "SYNTHETIC_FIXTURE"
  );
}

const CATEGORY_LABELS: Record<TriageCategory, string> = {
  PERSONAL: "个人邮箱",
  ACADEMIC_INSTITUTIONAL: "学校/机构邮箱",
  CORPORATE: "企业邮箱",
  UNKNOWN: "邮箱类型待确认",
};

const DISPOSITION_LABELS = {
  PARALLEL_DISTRIBUTE: "确认有效并并行分发",
  SALES_ONLY: "确认有效，直接分配销售",
  REQUEST_INFORMATION: "请求客户补充信息",
  RISK_REVIEW: "转风险审核",
  LINK_EXISTING_CASE: "关联已有案件",
  MERGE_DUPLICATE: "合并重复询单",
  MARK_INVALID: "不是客户询单，已保留在邮件智能复核记录中",
  MARK_INVALID_INQUIRY: "无效询单，已关闭",
  HANDLED_ELSEWHERE: "已在 Gmail/他处处理，已关闭",
} as const;

/**
 * The queue list may close an already-working real Gmail inquiry without opening the workbench.
 * It reuses the same server-side disposition and the same narrow eligibility the server enforces:
 * only a deterministic customer-inquiry nature may be retained as "handled elsewhere".
 */
function listHandledElsewhereEligible(item: TriageInquiry): boolean {
  if (item.dataOrigin !== "REAL_GMAIL") return false;
  if (item.status === "CASE_CREATED" || item.status === "CLOSED") return false;
  return ["NEW_INQUIRY", "GENERAL_QUESTION", "UNKNOWN"].includes(
    String(item.emailNature ?? "UNKNOWN").toUpperCase(),
  );
}

function listCustomerTier(
  value: string | undefined,
): "A" | "B" | "C" | "D" | "E" | "UNKNOWN" {
  return value === "A" ||
    value === "B" ||
    value === "C" ||
    value === "D" ||
    value === "E"
    ? value
    : "UNKNOWN";
}

type WorkbenchOperationState =
  | "LOADING"
  | "LOAD_FAILED"
  | "READY"
  | "CONFIRMING"
  | "SUBMITTING"
  | "MERGED_SUCCESS"
  | "DISTRIBUTED_SUCCESS"
  | "STALE";

interface SuccessReceipt {
  id: string;
  title: string;
  caseId: string | null;
  caseNumber: string | null;
  sales: string;
  sourcing: string;
  taskCount: number;
}

const VALIDATION_STATE_LABELS: Record<string, string> = {
  VERIFIED: "已验证",
  PROVIDED_UNVERIFIED: "客户提供但未独立验证",
  RESOLVED_FROM_INTERNAL_DATA: "由内部可靠数据补全",
  INFERRED: "推断值",
  MISSING_NON_BLOCKING: "缺失但不阻塞",
  MISSING_BLOCKING: "缺失且阻塞",
  CONFLICT: "存在冲突",
};

const REAL_FIELD_LABELS: Record<string, string> = {
  "Source / Site": "来源 / 站点",
  Product: "产品",
  CAS: "CAS",
  Quantity: "数量",
  Unit: "单位",
  Email: "邮箱",
  Company: "公司 / 机构",
  "Full Name": "联系人姓名",
  "Email Domain": "邮箱域名",
  Country: "国家 / 地区",
  "Intended Use": "用途",
  "Project Details": "项目说明",
};

const REAL_EVIDENCE_LABELS: Record<string, string> = {
  "Existing Contact → Existing Organization": "已有联系人已关联现有组织",
  "Exact verified email domain → Organization":
    "已验证的邮箱域名精确匹配现有组织",
  "Exact domain matched more than one active organization":
    "邮箱域名匹配到多个有效组织，需要人工确认",
  "历史询单组织信息 → Existing Organization":
    "历史询单中的组织信息已匹配现有组织",
  "Academic/Institutional domain recognized": "已识别学校 / 机构邮箱域名",
  "Company/Institution form value": "客户填写了公司 / 机构名称",
  "Company/Institution form value → Existing Organization":
    "客户填写的公司 / 机构名称已匹配现有组织",
  "Company/Institution form value; no internal match":
    "客户填写了公司 / 机构名称，但内部没有匹配记录",
  "Corporate domain is syntactically valid; no internal match":
    "企业邮箱域名格式有效，但内部没有匹配记录",
  "No reliable internal organization resolution": "内部暂无可靠的组织解析结果",
  "Source Integration / Site Registry": "来源集成与站点登记信息一致",
  "BenchChem Product Page / source record":
    "BenchChem 产品页与来源记录已提供产品信息",
  "CAS format and checksum rule": "CAS 格式与校验位规则通过",
  "CAS is required for reliable chemical matching": "可靠的化学品匹配需要 CAS",
  "Numeric quantity and unit were provided": "已提供可解析的数值、数量与单位",
  "Quantity is required for sourcing": "查货前必须提供数量",
  "Unit parsed from source quantity and normalized":
    "已从来源数量中解析并标准化单位",
  "Unit is required for sourcing": "查货前必须提供单位",
  "Source email format and domain parsed": "来源邮箱格式有效，域名已解析",
  "Email format is invalid": "邮箱格式无效",
  "Customer-provided source field": "客户在来源记录中提供",
  "Not required for ordinary triage": "普通分诊不要求必须提供",
  "Source email normalized and domain extracted": "来源邮箱已标准化并提取域名",
  "Country can be resolved later from organization":
    "国家 / 地区可在确认组织后补全",
  "Not required for ordinary sourcing unless risk rules require it":
    "普通查货不要求必须提供，风险规则命中时除外",
  "Not independently required for ordinary triage": "普通分诊不单独要求提供",
  "Organization requires human confirmation before business action":
    "执行正式业务操作前需要人工确认组织",
  "Source risk rule matched": "来源数据命中风险规则",
  "Existing organization and open case candidate match this inquiry":
    "现有组织与开放案件候选均匹配本次询单",
  "Organization resolved from internal CRM data": "已通过 CRM 内部数据解析组织",
  "Product, CAS, quantity and unit are usable":
    "产品、CAS、数量和单位均可用于后续处理",
  "Source facts": "来源事实",
  "Field validation": "字段验证",
  "Organization resolution": "组织解析",
  "Deterministic rules": "确定性规则",
  "Existing Organization": "现有组织",
  "CAS in case subject": "案件主题包含相同 CAS",
  "Current quantity/specification requires human comparison":
    "当前数量与规格需要人工比对",
  "Resolved academic organization": "已解析为学校 / 机构组织",
  "Resolved distributor organization": "已解析为经销商组织",
  "Resolved corporate organization": "已解析为企业组织",
  "Personal email without resolved organization": "个人邮箱尚未解析到组织",
  "Insufficient deterministic identity evidence": "确定性身份识别证据不足",
  "Real Data Shadow Snapshot": "真实数据只读影子快照",
};

function realFieldLabel(value: unknown): string {
  const text = String(value ?? "");
  return REAL_FIELD_LABELS[text] ?? text;
}

function realEvidenceLabel(value: unknown): string {
  const text = String(value ?? "");
  return REAL_EVIDENCE_LABELS[text] ?? text;
}

function realEvidenceSummary(value: unknown): string {
  return Array.isArray(value)
    ? value.map(realEvidenceLabel).join("；")
    : realEvidenceLabel(value);
}

function customerGradeLabel(value: string | undefined): string {
  if (!value || value === "UNKNOWN") return "等级待确认";
  return `等级 ${value}`;
}

type Disposition = keyof typeof DISPOSITION_LABELS;

/**
 * Canonical mail-nature judgements an operator can filter the queue by. The order mirrors the
 * persisted canonical nature list, with UNKNOWN last so the "model could not decide" bucket is
 * reachable but not in the way of the real business outcomes.
 */
const EMAIL_NATURE_FILTER_OPTIONS = [
  "NEW_INQUIRY",
  "EXISTING_CASE_REPLY",
  "STAFF_REPLY",
  "PURCHASE_ORDER",
  "GENERAL_QUESTION",
  "SUPPLIER_OR_SERVICE_OUTREACH",
  "AUTOMATED_NOTIFICATION",
  "SPAM_OR_PHISHING",
  "UNKNOWN",
] as const;

/** An item without a persisted nature is exactly the "未识别" bucket operators must be able to see. */
function itemEmailNature(item: {
  readonly emailNature?: string | null;
}): string {
  return item.emailNature ?? "UNKNOWN";
}

const EXCEPTION_LABELS: Record<TriageExceptionType, string> = {
  EMAIL_TYPE_UNKNOWN: "邮箱类型待确认",
  MESSAGE_NATURE_UNKNOWN: "邮件性质待确认",
  CUSTOMER_IDENTITY_UNKNOWN: "客户身份待确认",
  CUSTOMER_IDENTITY_CONFLICT: "客户身份冲突",
  HUMAN_REVIEW_REQUIRED: "需要人工业务复核",
  AI_JUDGMENT_FAILED: "AI 判断失败",
  AI_JUDGMENT_REVIEW: "AI 判断待复核",
  NO_TRIAGE_OWNER: "无分诊负责人",
  ROUTING_FAILED: "路由失败",
  DUPLICATE_CONFLICT: "重复冲突",
  RISK_REVIEW_REQUIRED: "风险待确认",
  PRODUCT_INFORMATION_MISSING: "产品信息缺失",
  QUANTITY_INFORMATION_MISSING: "数量信息缺失",
  EXISTING_CASE_CONFLICT: "现有案件关联冲突",
  ASSIGNMENT_FAILED: "分配失败",
};

function realExceptionTypes(item: RealShadowInquiry): TriageExceptionType[] {
  const types = new Set<TriageExceptionType>();
  if (item.emailCategory === "UNKNOWN") types.add("EMAIL_TYPE_UNKNOWN");
  if (!item.customerBusinessProfile.organizationName)
    types.add("CUSTOMER_IDENTITY_UNKNOWN");
  if (item.triageDecisionProfile?.analysisStatus === "FAILED")
    types.add("AI_JUDGMENT_FAILED");
  if (
    item.triageDecisionProfile?.analysisStatus === "READY" &&
    item.triageDecisionProfile.requiresReview
  )
    types.add("AI_JUDGMENT_REVIEW");
  if (item.duplicateProfile.requiresReview) types.add("DUPLICATE_CONFLICT");
  if (!item.inquiryDetailProfile.productIdentityResolved)
    types.add("PRODUCT_INFORMATION_MISSING");
  if (!item.inquiryDetailProfile.quantity)
    types.add("QUANTITY_INFORMATION_MISSING");
  if (
    item.productRiskProfile.riskState === "CONFIRMED_RISK" ||
    item.distributionDecision.action === "COMPLIANCE_REVIEW"
  )
    types.add("RISK_REVIEW_REQUIRED");
  if (
    item.exceptionReasons.some((reason) =>
      /existing case|现有案件|association/i.test(reason),
    )
  )
    types.add("EXISTING_CASE_CONFLICT");
  if (
    item.exceptionReasons.some((reason) => /assignment|分配失败/i.test(reason))
  )
    types.add("ASSIGNMENT_FAILED");
  if (item.exceptionReasons.some((reason) => /routing|路由失败/i.test(reason)))
    types.add("ROUTING_FAILED");
  return [...types];
}

function exceptionSeverity(types: readonly TriageExceptionType[]): string {
  if (
    types.some((type) =>
      ["RISK_REVIEW_REQUIRED", "ROUTING_FAILED", "ASSIGNMENT_FAILED"].includes(
        type,
      ),
    )
  )
    return "高";
  if (
    types.some((type) =>
      ["CUSTOMER_IDENTITY_CONFLICT", "DUPLICATE_CONFLICT"].includes(type),
    )
  )
    return "中";
  return "待分级";
}

const padInquiryTimePart = (value: number) => String(value).padStart(2, "0");

/**
 * Inquiry arrival time for the queue list, rendered in the operator's local time zone so a queue row
 * and its Gmail thread show the same clock time. Gmail-backed inquiries store the mail arrival
 * instant (Gmail internalDate) in `sourceSubmittedAt`.
 */
export function formatInquirySubmittedAt(
  value: string | null | undefined,
): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const date = `${parsed.getFullYear()}-${padInquiryTimePart(parsed.getMonth() + 1)}-${padInquiryTimePart(parsed.getDate())}`;
  const time = `${padInquiryTimePart(parsed.getHours())}:${padInquiryTimePart(parsed.getMinutes())}`;
  return `${date} ${time}`;
}

function inquiryAge(value: string): string {
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(value).getTime()) / 60_000),
  );
  if (minutes < 60) return `${minutes} 分钟`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} 小时`;
  return `${Math.floor(minutes / (24 * 60))} 天`;
}

const EVIDENCE_SOURCE_LABELS: Record<string, string> = {
  DETERMINISTIC_RULE: "确定性规则",
  INTERNAL_FACT: "内部业务事实",
  INTERNAL_MATCHING: "内部匹配结果",
  INTERNAL_HISTORY: "内部历史记录",
};

export function queueProductDisplay(
  product: string | null | undefined,
  cas: string | null | undefined,
): string | null {
  const normalized = product?.replace(/\s+/g, " ").trim() || "";
  if (
    normalized &&
    normalized.length <= 300 &&
    !/\b(?:could you please|provide (?:me|us) with|price\s*(?:&|and)\s*lead\s*time|best regards|disclaimer)\b/i.test(
      normalized,
    )
  )
    return normalized;
  const normalizedCas = cas?.trim();
  return normalizedCas ? `CAS ${normalizedCas}` : null;
}

export function queueQuantityDisplay(
  quantity: string | null | undefined,
): string | null {
  const normalized = quantity?.replace(/\s+/g, " ").trim() || "";
  if (!normalized || normalized.length > 120) return null;
  if (
    /\b(?:price\s*(?:&|and)\s*lead\s*time|best regards|disclaimer|could you please|provide (?:me|us) with)\b/i.test(
      normalized,
    )
  )
    return null;
  return normalized;
}

export function queueRiskSummary(
  risk: RealShadowInquiry["productRiskProfile"],
  productIdentityResolved: boolean,
): string {
  if (!productIdentityResolved) return "产品信息待补充，补充后再判断风险";
  if (risk.gasBusinessState === "CONFIRMED_GAS") return "气体产品，需人工处理";
  if (
    risk.overallDecision === "DO_NOT_PROCESS" ||
    risk.riskState === "CONFIRMED_RISK"
  )
    return "存在已确认风险，需人工处理";
  if (
    risk.dangerousGoods === "YES" ||
    ["SPECIAL", "DIFFICULT", "PENDING"].includes(risk.transportRisk) ||
    risk.riskState === "INSUFFICIENT_EVIDENCE"
  )
    return "查货需继续核查产品风险";
  if (risk.intelligence?.broadWebSearchExecuted !== true)
    return "仅完成现有资料分析，查货需继续核查";
  // A clean risk check is the default outcome, not a disposition: the queue row stays silent
  // instead of repeating a no-risk conclusion on every line.
  return "";
}

export function isActionStale(cause: unknown): boolean {
  if (cause instanceof CoreApiError) return cause.code === "ACTION_STALE";
  return cause instanceof Error && cause.message.includes("ACTION_STALE");
}

function collapseMergedInquiryRows<
  T extends {
    mergedGroup?: { groupKey: string } | null;
    mergedGroupKey?: string;
  },
>(items: T[]): T[] {
  const seenGroups = new Set<string>();
  return items.filter((item) => {
    const groupKey = item.mergedGroup?.groupKey ?? item.mergedGroupKey;
    if (!groupKey) return true;
    if (seenGroups.has(groupKey)) return false;
    seenGroups.add(groupKey);
    return true;
  });
}

export function forwardableMergedInquiryItems(
  detail: TriageBusinessWorkbenchDetail,
) {
  return (
    detail.mergedGroup?.items.filter(
      (member) =>
        member.processingStatus === "PENDING" && member.riskState !== "BLOCKED",
    ) ?? []
  );
}

function subjectRefOf(item: TriageInquiry): TriageWorkbenchSubjectRef {
  if (item.mergedGroupKey && item.dataOrigin !== "REAL_GMAIL")
    return { kind: "INQUIRY_GROUP", id: item.mergedGroupKey };
  return (
    item.subjectRef ?? {
      kind: item.isRealShadow ? "SHADOW_SNAPSHOT" : "FORMAL_INQUIRY",
      id: item.realShadowId ?? item.inquiryId,
    }
  );
}

function directFormalInquiryLinkItem(inquiryId: string): TriageInquiry {
  return {
    inquiryId,
    inquiryNumber: "邮件通信记录",
    channel: "GMAIL",
    dataOrigin: "REAL_GMAIL",
    subjectRef: { kind: "FORMAL_INQUIRY", id: inquiryId },
    workbenchExperience: "UNIFIED_BUSINESS_V1",
    sourceSite: { id: "direct-link", code: "PRIMARY_SITE", name: "EvitaChem" },
    sourceDomain: "evitachem.com",
    email: "",
    emailClassification: {
      category: "UNKNOWN",
      source: "DIRECT_LINK",
      confidence: "UNKNOWN",
      evidence: [],
    },
    customerCandidate: "邮件通信记录",
    product: null,
    cas: null,
    quantity: null,
    status: "CLOSED",
    processingStage: "COMPLETED",
    priority: "NORMAL",
    lockVersion: 0,
    risk: "NOT_APPLICABLE",
    caseId: null,
    overdue: false,
    triageReview: null,
    triageRouting: null,
    exceptionTypes: [],
  };
}

function isShadowSubject(item: TriageInquiry | undefined): boolean {
  return item
    ? ["SHADOW_SNAPSHOT", "INQUIRY_GROUP"].includes(subjectRefOf(item).kind)
    : false;
}

export function TriageWorkspace({
  exceptions = false,
  pilotMode = "REAL_SHADOW",
  enableAgentAutoRun = true,
  workspaceCode,
}: {
  exceptions?: boolean;
  pilotMode?: "REAL_SHADOW" | "CORE";
  enableAgentAutoRun?: boolean;
  workspaceCode?: string;
}) {
  const [category, setCategory] = useState<TriageCategory | undefined>(
    undefined,
  );
  const [realShadowItems, setRealShadowItems] = useState<RealShadowInquiry[]>(
    [],
  );
  const [exceptionType, setExceptionType] = useState<
    TriageExceptionType | undefined
  >(undefined);
  const [filterOpen, setFilterOpen] = useState(false);
  const [suppressionOpen, setSuppressionOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<
    "PENDING" | "PROCESSED" | "NO_ACTION"
  >("PENDING");
  const [gradeFilter, setGradeFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [natureFilter, setNatureFilter] = useState("");
  const [sortOrder, setSortOrder] = useState<"NEWEST" | "OLDEST">("NEWEST");
  const [overdue, setOverdue] = useState(false);
  const [search, setSearch] = useState("");
  const [data, setData] =
    useState<Awaited<ReturnType<typeof loadTriageInquiries>>>();
  const [scopes, setScopes] = useState<Array<Record<string, unknown>>>([]);
  const [selected, setSelected] = useState<TriageInquiry>();
  const [workbench, setWorkbench] = useState<Record<string, unknown>>();
  const [teams, setTeams] = useState<WorkforceTeam[]>([]);
  const [salesMembers, setSalesMembers] = useState<WorkforceMembership[]>([]);
  const [sourcingMembers, setSourcingMembers] = useState<WorkforceMembership[]>(
    [],
  );
  const [salesUserId, setSalesUserId] = useState("");
  const [sourcingUserId, setSourcingUserId] = useState("");
  const [disposition, setDisposition] = useState<Disposition>(
    "PARALLEL_DISTRIBUTE",
  );
  const [customerTier, setCustomerTier] = useState("UNKNOWN");
  const [emailCategory, setEmailCategory] = useState<TriageCategory>("UNKNOWN");
  const [existingCaseId, setExistingCaseId] = useState("");
  const [otherInquiryId, setOtherInquiryId] = useState("");
  const [distributionResult, setDistributionResult] =
    useState<Record<string, unknown>>();
  const [realDistributionResult, setRealDistributionResult] = useState<
    | RealShadowDistributionResult
    | RealShadowGroupDistributionResult
    | FormalInquiryDistributionResult
  >();
  const [mergeResult, setMergeResult] = useState<RealShadowMergeResult>();
  const [operationState, setOperationState] =
    useState<WorkbenchOperationState>("READY");
  const [successReceipts, setSuccessReceipts] = useState<SuccessReceipt[]>([]);
  const [listHandledElsewhere, setListHandledElsewhere] =
    useState<TriageInquiry | null>(null);
  const [listHandledElsewhereReason, setListHandledElsewhereReason] =
    useState<HandledElsewhereReason>("HANDLED_IN_GMAIL");
  const [reason, setReason] = useState("分诊人员已完成证据核对");
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState("");
  const [agentAnalysisError, setAgentAnalysisError] = useState("");
  const [notice, setNotice] = useState("");
  /**
   * Domains whose permanent Workspace mark this operator released in this session, keyed by
   * normalised domain, so the safety card can show the release instead of looking unresponsive.
   * The register itself — not this map — remains the source of truth.
   */
  const [releasedDomainMarks, setReleasedDomainMarks] = useState<
    Record<string, string>
  >({});
  const [queueCompleted, setQueueCompleted] = useState(false);
  const listScrollY = useRef(0);
  const queueScrollRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const unifiedWorkbenchCache = useRef(
    new Map<string, TriageBusinessWorkbenchDetail>(),
  );
  const agentEnsureInFlight = useRef(false);
  const agentEnsureAttempted = useRef(false);
  const refreshGeneration = useRef(0);

  const refresh = async () => {
    const generation = ++refreshGeneration.current;
    setBusy(true);
    setError("");
    try {
      if (pilotMode === "REAL_SHADOW") {
        // The formal Gmail queue is the operator's source of truth. Load it first so the
        // browser does not start a second, detail-heavy legacy shadow projection in parallel.
        // Real Shadow is only needed as the fallback queue when no real Gmail work exists.
        const coreQueue = await loadTriageInquiries({
          category,
          overdue,
          search,
          exception: exceptionType,
        });
        const hasRealGmail = coreQueue.items.some(
          (item) =>
            item.dataOrigin === "REAL_GMAIL" &&
            !isSyntheticFixtureQueueItem(item),
        );
        let real = hasRealGmail
          ? {
              mode: "REAL_DATA_READ_ONLY_SHADOW" as const,
              total: 0,
              items: [] as RealShadowInquiry[],
              review: { reviewed: 0, pending: 0 },
            }
          : await loadRealShadowInquiries();
        if (generation !== refreshGeneration.current) return;
        const needsAutomaticAnalysis = real.items.some((item) => {
          const processing = processingResultOf(item);
          const status = triageAgentProfileOf(item).status;
          return (
            processing.status === "PENDING" &&
            item.triageDecisionProfile?.source !== "SYNTHETIC_FIXTURE" &&
            ["NOT_STARTED", "FAILED", "STALE"].includes(status)
          );
        });
        if (
          enableAgentAutoRun &&
          needsAutomaticAnalysis &&
          !agentEnsureAttempted.current &&
          !agentEnsureInFlight.current
        ) {
          agentEnsureAttempted.current = true;
          agentEnsureInFlight.current = true;
          try {
            const result = await ensurePendingRealShadowAgents();
            if (generation !== refreshGeneration.current) return;
            if (result.failed > 0) setAgentAnalysisError("AI 分析暂未完成");
            real = await loadRealShadowInquiries();
            if (generation !== refreshGeneration.current) return;
          } catch {
            setAgentAnalysisError("AI 分析暂未完成");
          } finally {
            agentEnsureInFlight.current = false;
          }
        }
        if (
          !real.items.some(
            (item) =>
              processingResultOf(item).status === "PENDING" &&
              item.triageDecisionProfile?.source !== "SYNTHETIC_FIXTURE" &&
              ["NOT_STARTED", "FAILED", "STALE"].includes(
                triageAgentProfileOf(item).status,
              ),
          )
        ) {
          setAgentAnalysisError("");
        }
        if (generation !== refreshGeneration.current) return;
        setRealShadowItems(real.items);
        const normalizedSearch = search.trim().toLocaleLowerCase("zh-CN");
        const matchesRealFilters = (
          item: RealShadowInquiry,
          includeCategory: boolean,
        ) => {
          const exceptionTypes = realExceptionTypes(item);
          if (exceptions && exceptionTypes.length === 0) return false;
          if (
            exceptions &&
            exceptionType &&
            !exceptionTypes.includes(exceptionType)
          )
            return false;
          if (includeCategory && category && item.emailCategory !== category)
            return false;
          if (
            statusFilter === "PENDING" &&
            processingResultOf(item).status !== "PENDING"
          )
            return false;
          if (
            statusFilter === "PROCESSED" &&
            processingResultOf(item).status !== "PROCESSED"
          )
            return false;
          if (statusFilter === "NO_ACTION") return false;
          if (
            gradeFilter &&
            item.customerBusinessProfile.customerGrade !== gradeFilter
          )
            return false;
          if (
            countryFilter.trim() &&
            !String(item.customerBusinessProfile.country ?? "")
              .toLocaleLowerCase("zh-CN")
              .includes(countryFilter.trim().toLocaleLowerCase("zh-CN"))
          )
            return false;
          if (natureFilter && itemEmailNature(item) !== natureFilter)
            return false;
          if (!normalizedSearch) return true;
          return [
            item.anonymousReference,
            item.customerBusinessProfile.organizationName,
            item.inquiryDetailProfile.productName,
            item.inquiryDetailProfile.cas,
            item.inquiryDetailProfile.catalogNumber,
          ].some((value) =>
            String(value ?? "")
              .toLocaleLowerCase("zh-CN")
              .includes(normalizedSearch),
          );
        };
        const gmailItems = coreQueue.items.filter(
          (item) =>
            item.dataOrigin === "REAL_GMAIL" &&
            !isSyntheticFixtureQueueItem(item),
        );
        const operatorRealItems =
          gmailItems.length > 0
            ? real.items.filter((item) => !isSyntheticFixtureRealItem(item))
            : real.items;
        const visibleRealItems = operatorRealItems
          .filter((item) => matchesRealFilters(item, true))
          .sort((left, right) => {
            const direction = sortOrder === "NEWEST" ? -1 : 1;
            return (
              direction *
              (new Date(left.sourceTimestamp).getTime() -
                new Date(right.sourceTimestamp).getTime())
            );
          });
        const queueRealItems = collapseMergedInquiryRows(visibleRealItems);
        const allQueueItems = collapseMergedInquiryRows(operatorRealItems);
        const categoryCountItems = collapseMergedInquiryRows(
          operatorRealItems.filter((item) => matchesRealFilters(item, false)),
        );
        const gmailNoActionItems = gmailItems.filter(
          (item) =>
            item.businessSemantics?.recordKind === "COMMUNICATION_ONLY" &&
            item.status === "CLOSED",
        );
        const gmailProcessedBusinessItems = gmailItems.filter(
          (item) => item.status === "CASE_CREATED",
        );
        const gmailPendingItems = gmailItems.filter(
          (item) =>
            item.status !== "CASE_CREATED" &&
            !(
              item.businessSemantics?.recordKind === "COMMUNICATION_ONLY" &&
              item.status === "CLOSED"
            ),
        );
        const matchesGmailFilters = (
          item: TriageInquiry,
          includeCategory: boolean,
        ) => {
          if (exceptions && item.exceptionTypes.length === 0) return false;
          if (
            exceptions &&
            exceptionType &&
            !item.exceptionTypes.includes(exceptionType)
          )
            return false;
          if (
            includeCategory &&
            category &&
            item.emailClassification.category !== category
          )
            return false;
          const noAction =
            item.businessSemantics?.recordKind === "COMMUNICATION_ONLY" &&
            item.status === "CLOSED";
          const processed = item.status === "CASE_CREATED";
          if (statusFilter === "PENDING" && (processed || noAction))
            return false;
          if (statusFilter === "PROCESSED" && !processed) return false;
          if (statusFilter === "NO_ACTION" && !noAction) return false;
          if (gradeFilter && item.customerTier !== gradeFilter) return false;
          if (natureFilter && itemEmailNature(item) !== natureFilter)
            return false;
          return true;
        };
        const visibleGmailItems = collapseMergedInquiryRows(
          gmailItems.filter((item) => matchesGmailFilters(item, true)),
        );
        const gmailCategoryItems = collapseMergedInquiryRows(
          gmailItems.filter((item) => matchesGmailFilters(item, false)),
        );
        if (generation !== refreshGeneration.current) return;
        // An empty 待处理 queue keeps the operator's place: the tab stays selected and the
        // list shows an empty state. Switching to 已处理 on their behalf loses the position
        // they deliberately chose, and the 已处理 count already advertises that work exists.
        setData({
          items: [
            ...visibleGmailItems,
            ...queueRealItems.map((item) => ({
              inquiryId: item.id,
              realShadowId: item.id,
              isRealShadow: true,
              subjectRef: item.mergedGroup?.groupKey
                ? {
                    kind: "INQUIRY_GROUP" as const,
                    id: item.mergedGroup.groupKey,
                  }
                : (item.subjectRef ?? {
                    kind: "SHADOW_SNAPSHOT" as const,
                    id: item.id,
                  }),
              workbenchExperience: "UNIFIED_BUSINESS_V1" as const,
              primaryActionCapability: item.primaryActionCapability,
              mergedGroupKey: item.mergedGroup?.groupKey,
              mergedInquiryCount: item.mergedGroup?.inquiryCount,
              mergedProductCount: item.mergedGroup?.productCount,
              inquiryNumber: item.anonymousReference,
              sourceSite: {
                id: "real-shadow",
                code: item.sourceSite ?? "PRIMARY_SITE",
                name:
                  item.sourceSite === "PRIMARY_SITE"
                    ? "EvitaChem"
                    : item.sourceSite === "BENCHCHEM"
                      ? "BenchChem"
                      : (item.sourceSite ?? "EvitaChem"),
              },
              sourceDomain:
                (item.sourceSite ?? "PRIMARY_SITE") === "PRIMARY_SITE"
                  ? "evitachem.com"
                  : "www.benchchem.com",
              email: item.email,
              emailClassification: {
                category: item.emailCategory,
                source: "REAL_SHADOW",
                confidence: "REAL_DATA",
                evidence: ["真实数据只读影子快照"],
              },
              emailNature: item.emailNature,
              businessSemantics: item.businessSemantics,
              customerCandidate:
                item.customerBusinessProfile.organizationName ??
                "客户身份待确认",
              customerIdentityStatus:
                item.customerBusinessProfile.organizationIdentityStatus,
              customerIdentitySource:
                item.customerBusinessProfile.organizationNameSource,
              product: item.mergedGroup
                ? `${item.mergedGroup.productCount} 个产品的联合需求`
                : queueProductDisplay(
                    item.inquiryDetailProfile.productName,
                    item.inquiryDetailProfile.cas,
                  ),
              cas: item.inquiryDetailProfile.cas,
              quantity: queueQuantityDisplay(
                item.inquiryDetailProfile.quantity,
              ),
              batchType: item.batchType,
              status: processingResultOf(item).status,
              processingStage: "REAL_SHADOW",
              priority: "NORMAL",
              lockVersion: 1,
              risk:
                item.triageDecisionProfile?.overallStatus ??
                item.productRiskProfile.overallDecision,
              caseId: processingResultOf(item).caseId,
              overdue: false,
              triageReview: null,
              triageRouting: null,
              exceptionTypes: realExceptionTypes(item),
              customerTier: item.customerBusinessProfile.customerGrade,
              quality: item.customerBusinessProfile.quality,
              historyConclusion:
                item.distributionDecision.recentReusableHistory.status ===
                "REUSABLE"
                  ? "已有近期有效查货结果，可直接报价"
                  : item.distributionDecision.recentReusableHistory.status ===
                      "FAILED_HISTORY"
                    ? "近期失败记录不可复用"
                    : "当前 CRM 未匹配到可复用报价",
              recommendation: {
                dispositionType: item.distributionDecision.action,
                executionDisposition: item.distributionDecision.action,
                headline: item.distributionDecision.actionLabel,
                confidence: "业务规则",
                targetId: null,
                targetLabel:
                  item.distributionDecision.salesCandidate?.displayName ??
                  "只读影子建议",
              },
            })),
          ],
          total: visibleGmailItems.length + queueRealItems.length,
          counts: {
            PERSONAL:
              categoryCountItems.filter(
                (item) => item.emailCategory === "PERSONAL",
              ).length +
              gmailCategoryItems.filter(
                (item) => item.emailClassification.category === "PERSONAL",
              ).length,
            ACADEMIC_INSTITUTIONAL:
              categoryCountItems.filter(
                (item) => item.emailCategory === "ACADEMIC_INSTITUTIONAL",
              ).length +
              gmailCategoryItems.filter(
                (item) =>
                  item.emailClassification.category ===
                  "ACADEMIC_INSTITUTIONAL",
              ).length,
            CORPORATE:
              categoryCountItems.filter(
                (item) => item.emailCategory === "CORPORATE",
              ).length +
              gmailCategoryItems.filter(
                (item) => item.emailClassification.category === "CORPORATE",
              ).length,
            UNKNOWN:
              categoryCountItems.filter(
                (item) => item.emailCategory === "UNKNOWN",
              ).length +
              gmailCategoryItems.filter(
                (item) => item.emailClassification.category === "UNKNOWN",
              ).length,
          },
          exceptionCounts: {
            ...Object.fromEntries(
              Object.keys(EXCEPTION_LABELS).map((type) => [
                type,
                real.items.filter(
                  (item) =>
                    processingResultOf(item).status === "PENDING" &&
                    realExceptionTypes(item).includes(
                      type as TriageExceptionType,
                    ),
                ).length +
                  gmailItems.filter(
                    (item) =>
                      item.status !== "CASE_CREATED" &&
                      item.exceptionTypes.includes(type as TriageExceptionType),
                  ).length,
              ]),
            ),
          } as Record<TriageExceptionType, number>,
          statuses: {
            pending:
              (exceptions ? queueRealItems : allQueueItems).filter(
                (item) => processingResultOf(item).status === "PENDING",
              ).length + gmailPendingItems.length,
            inProgress: 0,
            processed:
              allQueueItems.filter(
                (item) => processingResultOf(item).status === "PROCESSED",
              ).length + gmailProcessedBusinessItems.length,
            noActionRequired: gmailNoActionItems.length,
            overdue: 0,
            needsAttention:
              queueRealItems.filter(
                (item) => processingResultOf(item).status === "PENDING",
              ).length + gmailPendingItems.length,
            highPriority: 0,
            dueSoon: 0,
            oneClickConfirm: queueRealItems.filter(
              (item) =>
                processingResultOf(item).status === "PENDING" &&
                item.distributionDecision.action === "ONE_CLICK_DISTRIBUTE",
            ).length,
            complexJudgment: queueRealItems.filter(
              (item) =>
                processingResultOf(item).status === "PENDING" &&
                (item.triageDecisionProfile?.requiresReview === true ||
                  item.productRiskProfile.riskState ===
                    "INSUFFICIENT_EVIDENCE"),
            ).length,
          },
        });
        setScopes([]);
        return;
      }
      const [result, scopeResult] = await Promise.all([
        loadTriageInquiries({
          category,
          overdue,
          search,
          exception: exceptionType,
        }),
        loadTriageScopes(),
      ]);
      if (generation !== refreshGeneration.current) return;
      setData(result);
      setScopes(scopeResult.items);
    } catch (cause) {
      setError(describeActionError(cause, "询单分诊数据加载失败"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, [
    category,
    overdue,
    exceptionType,
    pilotMode,
    statusFilter,
    gradeFilter,
    countryFilter,
    natureFilter,
    sortOrder,
    search,
    enableAgentAutoRun,
  ]);

  const agentRefreshKey = realShadowItems
    .map(
      (item) =>
        `${item.id}:${processingResultOf(item).status}:${triageAgentProfileOf(item).status}`,
    )
    .join("|");
  useEffect(() => {
    if (pilotMode !== "REAL_SHADOW") return;
    if (statusFilter !== "PENDING") return;
    const needsPolling = realShadowItems.some((item) => {
      if (processingResultOf(item).status !== "PENDING") return false;
      return ["QUEUED", "RUNNING"].includes(triageAgentProfileOf(item).status);
    });
    if (!needsPolling) return;
    const timer = window.setTimeout(() => void refresh(), 1_500);
    return () => window.clearTimeout(timer);
  }, [
    agentRefreshKey,
    pilotMode,
    statusFilter,
    category,
    overdue,
    exceptionType,
    gradeFilter,
    countryFilter,
    natureFilter,
    sortOrder,
    search,
  ]);

  const selectedAgentStatus = (
    workbench?.unifiedBusinessView as TriageBusinessWorkbenchDetail | undefined
  )?.triageAgentProfile?.status;
  useEffect(() => {
    const selectedSubject = selected ? subjectRefOf(selected) : undefined;
    if (
      selectedSubject?.kind !== "SHADOW_SNAPSHOT" ||
      !selectedAgentStatus ||
      !["QUEUED", "RUNNING"].includes(selectedAgentStatus)
    )
      return;
    const timer = window.setTimeout(() => {
      void loadTriageBusinessWorkbench(selectedSubject)
        .then((detail) => {
          unifiedWorkbenchCache.current.set(
            `${selectedSubject.kind}:${selectedSubject.id}`,
            detail,
          );
          setWorkbench({
            workbenchExperience: "UNIFIED_BUSINESS_V1",
            unifiedBusinessView: detail,
          });
          return refresh();
        })
        .catch(() => undefined);
    }, 1_500);
    return () => window.clearTimeout(timer);
  }, [selected?.inquiryId, selectedAgentStatus]);

  useEffect(() => {
    void workforceApi
      .teams()
      .then(async ({ items }) => {
        setTeams(items);
        const salesTeam = items.find((team) => team.teamType === "SALES");
        const sourcingTeam = items.find((team) => /SOURCING/i.test(team.code));
        if (salesTeam) {
          const { items: people } = await workforceApi.teamMembers(
            salesTeam.id,
          );
          setSalesMembers(people);
          setSalesUserId(people[0]?.userId ?? "");
        }
        if (sourcingTeam) {
          const { items: people } = await workforceApi.teamMembers(
            sourcingTeam.id,
          );
          setSourcingMembers(people);
          setSourcingUserId(people[0]?.userId ?? "");
        }
      })
      .catch(() => undefined);
  }, []);

  const openWorkbench = async (
    item: TriageInquiry,
    historyMode: "push" | "replace" | "none" = "push",
  ) => {
    setQueueCompleted(false);
    listScrollY.current = window.scrollY;
    const subject = subjectRefOf(item);
    const cacheKey = `${subject.kind}:${subject.id}`;
    const cached = unifiedWorkbenchCache.current.get(cacheKey);
    setWorkbench(
      cached
        ? {
            workbenchExperience: "UNIFIED_BUSINESS_V1",
            unifiedBusinessView: cached,
          }
        : undefined,
    );
    setBusy(true);
    setOperationState("LOADING");
    setRealDistributionResult(undefined);
    setMergeResult(undefined);
    setError("");
    try {
      const detail = await loadTriageBusinessWorkbench(subject);
      const experience =
        detail.workbenchExperience ?? item.workbenchExperience ?? "LEGACY_CORE";
      unifiedWorkbenchCache.current.set(cacheKey, detail);
      setSelected({
        ...item,
        subjectRef: subject,
        workbenchExperience: experience,
      });
      if (subject.kind === "FORMAL_INQUIRY") {
        setEmailCategory(item.emailClassification.category);
      }
      setDistributionResult(undefined);
      const decisionPackage = (detail.decisionPackage ?? {}) as Record<
        string,
        unknown
      >;
      const customerDecision = (decisionPackage.customerDecision ??
        {}) as Record<string, unknown>;
      const recommendedTier = String(customerDecision.tier ?? "UNKNOWN");
      if (["A", "B", "C", "D", "E"].includes(recommendedTier))
        setCustomerTier(recommendedTier);
      const recommended = (decisionPackage.recommendedDisposition ??
        {}) as Record<string, unknown>;
      const recommendedDisposition = String(
        recommended.executionDisposition ?? "",
      );
      if (
        Object.prototype.hasOwnProperty.call(
          DISPOSITION_LABELS,
          recommendedDisposition,
        )
      ) {
        setDisposition(recommendedDisposition as Disposition);
      }
      const recommendedTarget = (recommended.recommendedTarget ??
        null) as Record<string, unknown> | null;
      const recommendedSales = (recommended.recommendedSales ?? null) as Record<
        string,
        unknown
      > | null;
      const recommendedSourcing = (recommended.recommendedSourcing ??
        null) as Record<string, unknown> | null;
      if (
        recommendedDisposition === "LINK_EXISTING_CASE" &&
        recommendedTarget?.id
      )
        setExistingCaseId(String(recommendedTarget.id));
      if (recommendedDisposition === "MERGE_DUPLICATE" && recommendedTarget?.id)
        setOtherInquiryId(String(recommendedTarget.id));
      if (recommendedSales?.id) setSalesUserId(String(recommendedSales.id));
      if (recommendedSourcing?.id)
        setSourcingUserId(String(recommendedSourcing.id));
      setWorkbench(
        experience === "UNIFIED_BUSINESS_V1"
          ? {
              workbenchExperience: experience,
              unifiedBusinessView: detail,
            }
          : (detail as unknown as Record<string, unknown>),
      );
      setOperationState("READY");
      if (historyMode !== "none") {
        const method = historyMode === "replace" ? "replaceState" : "pushState";
        const url = new URL(window.location.href);
        url.searchParams.set("view", "inquiry-center");
        if (experience === "UNIFIED_BUSINESS_V1") {
          url.searchParams.set("selected", item.inquiryId);
          url.searchParams.delete("triage");
        } else {
          url.searchParams.set("triage", item.inquiryId);
          url.searchParams.delete("selected");
        }
        url.searchParams.delete("peek");
        window.history[method](null, "", url);
      }
    } catch (cause) {
      setError(describeActionError(cause, "分诊工作台加载失败"));
      setOperationState("LOAD_FAILED");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (pilotMode !== "REAL_SHADOW" || !data || selected) return;
    const selectedId = new URLSearchParams(window.location.search).get(
      "selected",
    );
    if (!selectedId) return;
    const item = data.items.find((entry) => entry.inquiryId === selectedId);
    void openWorkbench(item ?? directFormalInquiryLinkItem(selectedId), "none");
  }, [data, pilotMode, selected]);

  useEffect(() => {
    const restoreSelection = () => {
      if (pilotMode !== "REAL_SHADOW") return;
      const selectedId = new URLSearchParams(window.location.search).get(
        "selected",
      );
      if (!selectedId) {
        setSelected(undefined);
        setWorkbench(undefined);
        return;
      }
      const item = data?.items.find((entry) => entry.inquiryId === selectedId);
      if (selectedId !== selected?.inquiryId)
        void openWorkbench(
          item ?? directFormalInquiryLinkItem(selectedId),
          "none",
        );
    };
    window.addEventListener("popstate", restoreSelection);
    return () => window.removeEventListener("popstate", restoreSelection);
  }, [data, pilotMode, selected?.inquiryId]);

  const submitDecision = async (
    nextSalesUserId?: string,
    nextSourcingUserId?: string,
    sourcingStrategy?: HumanGmailSourcingStrategy,
    confirmedInquiryFields?: Record<string, string>,
    dispositionOverride?: Disposition,
    confirmedMessageNature?:
      | "NEW_INQUIRY"
      | "PURCHASE_ORDER"
      | "GENERAL_QUESTION",
    reasonOverride?: string,
    suppressSender?: boolean,
    ccSalesArchive?: boolean,
  ) => {
    if (!selected || !workbench) return;
    setBusy(true);
    setOperationState("SUBMITTING");
    setError("");
    setNotice("");
    try {
      if (isShadowSubject(selected)) {
        const realBusinessView =
          workbench.unifiedBusinessView as TriageBusinessWorkbenchDetail;
        const selectedSubject = subjectRefOf(selected);
        if (
          realBusinessView.productRiskProfile.gasBusinessState ===
            "GAS_CONFIRMATION_REQUIRED" &&
          realBusinessView.gasPrecheck?.status !== "PENDING"
        ) {
          const precheck = await createRealShadowGasPrecheck(
            selected.realShadowId ?? selected.inquiryId,
            {
              expectedActionVersion:
                realBusinessView.primaryActionCapability.actionVersion,
              ...(sourcingUserId ? { sourcingUserId } : {}),
            },
          );
          const refreshed = await loadTriageBusinessWorkbench(selectedSubject);
          setWorkbench((current) =>
            current ? { ...current, unifiedBusinessView: refreshed } : current,
          );
          setNotice(
            `已创建气态属性确认任务：${String(precheck.taskId ?? "已创建")}`,
          );
          setOperationState("READY");
          await refresh();
          return;
        }
        const pendingGroupItems =
          forwardableMergedInquiryItems(realBusinessView);
        if (realBusinessView.mergedGroup && pendingGroupItems.length) {
          const capability = realBusinessView.primaryActionCapability;
          if (
            capability.type !== "GROUP_FORWARD" &&
            capability.type !== "PARTIAL_FORWARD"
          )
            throw new Error("当前联合询单操作已变化，请刷新后重试");
          const groupResult = await distributeRealShadowGroup(
            selected.realShadowId ?? selected.inquiryId,
            {
              groupKey: realBusinessView.mergedGroup.groupKey,
              expectedAction: capability.type,
              actionVersion: capability.actionVersion,
              ...(salesUserId ? { salesUserId } : {}),
              ...(sourcingUserId ? { sourcingUserId } : {}),
            },
            `real-shadow-group-distribution-${realBusinessView.mergedGroup.groupKey}-${capability.actionVersion}`,
          );
          const refreshed = await loadTriageBusinessWorkbench(selectedSubject);
          unifiedWorkbenchCache.current.set(
            `${selectedSubject.kind}:${selectedSubject.id}`,
            refreshed,
          );
          setWorkbench((current) =>
            current ? { ...current, unifiedBusinessView: refreshed } : current,
          );
          await refresh();
          setRealDistributionResult(groupResult);
          setOperationState("DISTRIBUTED_SUCCESS");
          setSuccessReceipts((current) =>
            [
              {
                id: `${groupResult.groupKey}-${groupResult.processedAt}`,
                title: `已转发 ${groupResult.processedCount} 条询单`,
                caseId: groupResult.caseId,
                caseNumber: groupResult.caseNumber,
                sales: groupResult.salesAssignee?.displayName ?? "已分配",
                sourcing: groupResult.sourcingAssignee?.displayName ?? "已分配",
                taskCount: groupResult.tasks.length,
              },
              ...current,
            ].slice(0, 3),
          );
          return;
        }
        const result = await distributeRealShadowInquiry(
          selected.realShadowId ?? selected.inquiryId,
          {
            expectedAction: realBusinessView.distributionDecision.action,
            stabilityKey: realBusinessView.distributionDecision.stabilityKey,
            agentDecisionVersion:
              triageAgentProfileOf(realBusinessView).invocationId ??
              `no-agent-${realBusinessView.distributionDecision.stabilityKey}`,
            ...(salesUserId ? { salesUserId } : {}),
            ...(sourcingUserId ? { sourcingUserId } : {}),
          },
          `real-shadow-distribution-${selected.inquiryId}-${realBusinessView.distributionDecision.stabilityKey}`,
        );
        const processingResult = {
          status: "PROCESSED" as const,
          finalAction: result.finalDisposition,
          caseId: result.caseId,
          salesAssignmentId: result.salesAssignmentId,
          sourcingAssignmentId: result.sourcingAssignmentId,
          taskIds: result.taskIds,
          processedAt: result.processedAt,
          processedBy: result.processedBy,
        };
        const processedBusinessView = {
          ...realBusinessView,
          processingResult,
        };
        unifiedWorkbenchCache.current.set(
          `${selectedSubject.kind}:${selectedSubject.id}`,
          processedBusinessView,
        );
        setWorkbench((current) =>
          current
            ? { ...current, unifiedBusinessView: processedBusinessView }
            : current,
        );
        setRealShadowItems((current) =>
          current.map((item) =>
            item.id === selected.inquiryId
              ? {
                  ...item,
                  processingResult,
                }
              : item,
          ),
        );
        setData((current) =>
          current
            ? {
                ...current,
                items:
                  statusFilter === "PENDING"
                    ? current.items.filter(
                        (item) => item.inquiryId !== selected.inquiryId,
                      )
                    : current.items.map((item) =>
                        item.inquiryId === selected.inquiryId
                          ? {
                              ...item,
                              status: "PROCESSED",
                              caseId: result.caseId,
                            }
                          : item,
                      ),
                statuses: {
                  ...current.statuses,
                  pending: Math.max(0, current.statuses.pending - 1),
                  needsAttention: Math.max(
                    0,
                    (current.statuses.needsAttention ??
                      current.statuses.pending) - 1,
                  ),
                  processed: current.statuses.processed + 1,
                  complexJudgment: realBusinessView.triageDecisionProfile
                    ?.requiresReview
                    ? Math.max(0, (current.statuses.complexJudgment ?? 0) - 1)
                    : current.statuses.complexJudgment,
                  oneClickConfirm:
                    selected.recommendation?.dispositionType ===
                    "ONE_CLICK_DISTRIBUTE"
                      ? Math.max(0, (current.statuses.oneClickConfirm ?? 0) - 1)
                      : current.statuses.oneClickConfirm,
                },
                exceptionCounts: current.exceptionCounts
                  ? {
                      ...current.exceptionCounts,
                      DUPLICATE_CONFLICT: realBusinessView.duplicateProfile
                        .requiresReview
                        ? Math.max(
                            0,
                            (current.exceptionCounts.DUPLICATE_CONFLICT ?? 0) -
                              1,
                          )
                        : current.exceptionCounts.DUPLICATE_CONFLICT,
                      RISK_REVIEW_REQUIRED: realBusinessView
                        .triageDecisionProfile?.requiresReview
                        ? Math.max(
                            0,
                            (current.exceptionCounts.RISK_REVIEW_REQUIRED ??
                              0) - 1,
                          )
                        : current.exceptionCounts.RISK_REVIEW_REQUIRED,
                    }
                  : current.exceptionCounts,
              }
            : current,
        );
        setSelected((current) =>
          current
            ? { ...current, status: "PROCESSED", caseId: result.caseId }
            : current,
        );
        setNotice(
          result.outcome === "ALREADY_PROCESSED"
            ? "该询单已处理，已刷新现有结果。"
            : `处理成功：${distributionResultLabel(result.finalDisposition)}`,
        );
        setRealDistributionResult(result);
        setOperationState("DISTRIBUTED_SUCCESS");
        setSuccessReceipts((current) =>
          [
            {
              id: `${result.snapshotId}-${result.processedAt}`,
              title: "已转发 1 条询单",
              caseId: result.caseId,
              caseNumber: result.caseNumber,
              sales:
                realBusinessView.distributionDecision.salesCandidate
                  ?.displayName ?? "已分配",
              sourcing:
                realBusinessView.distributionDecision.sourcingCandidate
                  ?.displayName ?? "按策略跳过",
              taskCount: result.taskIds.length,
            },
            ...current,
          ].slice(0, 3),
        );
        return;
      }
      const salesTeam = teams.find((team) => team.teamType === "SALES");
      const sourcingTeam = teams.find((team) => /SOURCING/i.test(team.code));
      const formalBusinessView = workbench.unifiedBusinessView as
        | TriageBusinessWorkbenchDetail
        | undefined;
      const effectiveSalesUserId =
        nextSalesUserId ||
        salesUserId ||
        formalBusinessView?.distributionDecision.salesCandidate?.userId ||
        salesMembers[0]?.userId ||
        "";
      const effectiveSourcingUserId =
        nextSourcingUserId ||
        sourcingUserId ||
        formalBusinessView?.distributionDecision.sourcingCandidate?.userId ||
        sourcingMembers[0]?.userId ||
        "";
      const selectedSalesMember = salesMembers.find(
        (member) => member.userId === effectiveSalesUserId,
      );
      const selectedSourcingMember = sourcingMembers.find(
        (member) => member.userId === effectiveSourcingUserId,
      );
      // The queue row can be older than the workbench on screen: saving the customer profile or
      // any background analysis bumps the inquiry version, and submitting the row's stale version
      // was refused with "询单已被其他操作更新，请刷新后重试". Always submit the version the
      // server returned with the workbench we are acting on.
      const expectedVersion =
        formalBusinessView?.lifecycle?.processingVersion ??
        selected.lockVersion;
      const realGmailFormal =
        formalBusinessView?.subject?.kind === "FORMAL_INQUIRY" &&
        formalBusinessView.subject.dataOrigin === "REAL_GMAIL";
      const isOrderReview =
        formalBusinessView?.businessSemantics?.recordKind === "ORDER_REVIEW" ||
        formalBusinessView?.emailIntelligence?.orderReview?.isOrderCandidate ===
          true;
      const effectiveDisposition: Disposition =
        dispositionOverride ??
        (realGmailFormal
          ? isOrderReview
            ? "SALES_ONLY"
            : "PARALLEL_DISTRIBUTE"
          : disposition);
      if (
        (effectiveDisposition === "PARALLEL_DISTRIBUTE" ||
          effectiveDisposition === "SALES_ONLY") &&
        (!salesTeam || !effectiveSalesUserId)
      ) {
        throw new Error("请选择具有有效成员资格的销售负责人");
      }
      if (
        effectiveDisposition === "PARALLEL_DISTRIBUTE" &&
        (!sourcingTeam || !effectiveSourcingUserId)
      ) {
        throw new Error("请选择具有有效成员资格的查货负责人");
      }
      const recommendation = (workbench.recommendation ?? {}) as Record<
        string,
        unknown
      >;
      const distribution =
        (effectiveDisposition === "PARALLEL_DISTRIBUTE" ||
          effectiveDisposition === "SALES_ONLY") &&
        salesTeam &&
        effectiveSalesUserId
          ? {
              expectedVersion,
              priority: selected.priority,
              note: reason,
              salesTeamId: salesTeam.id,
              salesUserId: effectiveSalesUserId,
              salesAcceptanceDueAt: new Date(
                Date.now() + 4 * 60 * 60 * 1000,
              ).toISOString(),
              salesProcessingDueAt: new Date(
                Date.now() + 24 * 60 * 60 * 1000,
              ).toISOString(),
              salesReason: "基于当前客户与产品证据的人工分发",
              sourcingRequired: effectiveDisposition === "PARALLEL_DISTRIBUTE",
              sourcingTeamId: sourcingTeam?.id,
              sourcingUserId: effectiveSourcingUserId || undefined,
              sourcingAcceptanceDueAt: new Date(
                Date.now() + 4 * 60 * 60 * 1000,
              ).toISOString(),
              sourcingProcessingDueAt: new Date(
                Date.now() + 48 * 60 * 60 * 1000,
              ).toISOString(),
              sourcingStrategy:
                sourcingStrategy ??
                (formalBusinessView?.distributionDecision.recentReusableHistory
                  .status === "REUSABLE"
                  ? "REVIEW_REUSABLE_HISTORY"
                  : "RECHECK_SUPPLY"),
              sourcingReason:
                sourcingStrategy === "REVIEW_REUSABLE_HISTORY"
                  ? "分诊人员选择复核可复用的历史供货信息"
                  : "分诊人员选择重新确认价格、供应商与供货条件",
              // Transition-period convenience: unchecking drops the shared sales archive copy for
              // this distribution and every later notification of the case.
              archiveCc: ccSalesArchive !== false,
            }
          : undefined;
      const result = await decideTriage(
        selected.inquiryId,
        {
          expectedVersion,
          emailCategory,
          customerTier,
          disposition: effectiveDisposition,
          reason: reasonOverride ?? reason,
          ...(realGmailFormal
            ? {
                confirmedMessageNature:
                  confirmedMessageNature ??
                  (effectiveDisposition === "MARK_INVALID"
                    ? "GENERAL_QUESTION"
                    : effectiveDisposition === "MARK_INVALID_INQUIRY"
                      ? "NEW_INQUIRY"
                      : isOrderReview
                        ? "PURCHASE_ORDER"
                        : "NEW_INQUIRY"),
              }
            : {}),
          confirmedFields: {
            source: realGmailFormal
              ? "EVITACHEM_GMAIL_HUMAN_CONFIRMATION"
              : "TRIAGE_UAT",
            synthetic: !realGmailFormal,
            ...(confirmedInquiryFields ?? {}),
          },
          ...(existingCaseId ? { existingCaseId } : {}),
          ...(otherInquiryId ? { otherInquiryId } : {}),
          ...(suppressSender ? { suppressSender: true } : {}),
          distribution,
        },
        `triage-decision-${selected.inquiryId}-${crypto.randomUUID()}`,
      );
      const suppressionEntry = result.senderSuppression as
        | { displayValue?: string }
        | undefined;
      setNotice(
        suppressionEntry
          ? `已提交：${DISPOSITION_LABELS[effectiveDisposition]}；已将 ${suppressionEntry.displayValue ?? "该发件人"} 加入不再合作名单，后续询单将自动判为无效`
          : `已提交：${DISPOSITION_LABELS[effectiveDisposition]}`,
      );
      if (result.distribution && typeof result.distribution === "object") {
        setDistributionResult(result.distribution as Record<string, unknown>);
        if (isFormalInquiryDistributionResult(result.distribution)) {
          const formalResult = result.distribution;
          setRealDistributionResult(formalResult);
          setNotice(
            formalResult.outcome === "ALREADY_PROCESSED"
              ? `该询单已处理，已加载案件 ${formalResult.caseNumber}。`
              : isOrderReview
                ? `订单复核已提交：案件 ${formalResult.caseNumber} 已建立，销售通知已进入发送队列。`
                : `分发成功：案件 ${formalResult.caseNumber} 已建立，销售与查货通知已进入发送队列。`,
          );
          setSuccessReceipts((current) =>
            [
              {
                id: `${formalResult.inquiryId}-${formalResult.processedAt}`,
                title: isOrderReview
                  ? "已提交销售订单复核"
                  : "已确认新询价并完成内部分发",
                caseId: formalResult.caseId,
                caseNumber: formalResult.caseNumber,
                sales: selectedSalesMember?.user?.displayName ?? "已分配",
                sourcing: selectedSourcingMember?.user?.displayName ?? "已分配",
                taskCount: formalResult.taskIds.length,
              },
              ...current,
            ].slice(0, 3),
          );
        }
      }
      const selectedSubject = subjectRefOf(selected);
      const refreshed = await loadTriageBusinessWorkbench(selectedSubject);
      unifiedWorkbenchCache.current.set(
        `${selectedSubject.kind}:${selectedSubject.id}`,
        refreshed,
      );
      setWorkbench({
        workbenchExperience: refreshed.workbenchExperience,
        unifiedBusinessView: refreshed,
      });
      setSelected((current) =>
        current
          ? {
              ...current,
              status:
                refreshed.lifecycle?.status === "PROCESSED"
                  ? "CASE_CREATED"
                  : current.status,
              caseId: refreshed.processingResult?.caseId ?? current.caseId,
            }
          : current,
      );
      setOperationState(
        result.distribution &&
          (refreshed.processingResult?.status === "PROCESSED" ||
            isFormalInquiryDistributionResult(result.distribution))
          ? "DISTRIBUTED_SUCCESS"
          : "READY",
      );
      await refresh();
    } catch (cause) {
      setError(describeActionError(cause, "分诊处置提交失败"));
      setOperationState(isActionStale(cause) ? "STALE" : "READY");
    } finally {
      setBusy(false);
    }
  };

  const mergeRelated = async (
    candidateSnapshotIds: string[],
    actionVersion?: string,
  ) => {
    if (!selected || !candidateSnapshotIds.length) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const capability = (
        workbench?.unifiedBusinessView as
          | TriageBusinessWorkbenchDetail
          | undefined
      )?.primaryActionCapability;
      const effectiveVersion = actionVersion ?? capability?.actionVersion;
      if (capability?.type !== "MERGE" || !effectiveVersion)
        throw new Error("当前合并建议已变化，请刷新后重试");
      const shadow = isShadowSubject(selected);
      const rawResult = shadow
        ? await mergeRealShadowInquiries(
            selected.realShadowId ?? selected.inquiryId,
            {
              candidateSnapshotIds,
              actionVersion: effectiveVersion,
            },
            `real-shadow-merge-${selected.inquiryId}-${candidateSnapshotIds.slice().sort().join("-")}`,
          )
        : await mergeFormalInquiries(
            selected.inquiryId,
            {
              candidateInquiryIds: candidateSnapshotIds,
              actionVersion: effectiveVersion,
            },
            `formal-inquiry-merge-${selected.inquiryId}-${candidateSnapshotIds.slice().sort().join("-")}`,
          );
      const result: RealShadowMergeResult = shadow
        ? (rawResult as RealShadowMergeResult)
        : {
            status: rawResult.status,
            caseId: rawResult.caseId,
            sourceSnapshotCount: Number(rawResult.sourceInquiryCount),
            sourceInquiryCount: Number(rawResult.sourceInquiryCount),
            originalSourcesPreserved: rawResult.originalSourcesPreserved,
            duplicateEffect: 0,
          };
      setNotice(
        `已将 ${result.sourceSnapshotCount} 条询单合并为一个联合需求；原始询单与邮件依据仍分别保留。`,
      );
      setMergeResult(result);
      const selectedSubject = subjectRefOf(selected);
      const anchorDetail = await loadTriageBusinessWorkbench(selectedSubject);
      const groupSubject: TriageWorkbenchSubjectRef | undefined =
        shadow && anchorDetail.mergedGroup?.groupKey
          ? {
              kind: "INQUIRY_GROUP",
              id: anchorDetail.mergedGroup.groupKey,
            }
          : undefined;
      const refreshed = groupSubject
        ? await loadTriageBusinessWorkbench(groupSubject)
        : anchorDetail;
      unifiedWorkbenchCache.current.set(
        `${selectedSubject.kind}:${selectedSubject.id}`,
        anchorDetail,
      );
      if (groupSubject)
        unifiedWorkbenchCache.current.set(
          `${groupSubject.kind}:${groupSubject.id}`,
          refreshed,
        );
      setWorkbench((current) =>
        current ? { ...current, unifiedBusinessView: refreshed } : current,
      );
      if (groupSubject)
        setSelected((current) =>
          current
            ? {
                ...current,
                subjectRef: groupSubject,
                mergedGroupKey: groupSubject.id,
                mergedInquiryCount: refreshed.subject?.memberCount,
                mergedProductCount: refreshed.subject?.productCount,
              }
            : current,
        );
      else
        setSelected((current) =>
          current
            ? {
                ...current,
                mergedGroupKey: anchorDetail.mergedGroup?.groupKey,
                mergedInquiryCount: anchorDetail.subject?.memberCount,
                mergedProductCount: anchorDetail.subject?.productCount,
                lockVersion:
                  anchorDetail.lifecycle?.processingVersion ??
                  current.lockVersion,
              }
            : current,
        );
      await refresh();
      setOperationState("MERGED_SUCCESS");
    } catch (cause) {
      setError(describeActionError(cause, "合并处理失败"));
      setOperationState(isActionStale(cause) ? "STALE" : "READY");
    } finally {
      setBusy(false);
    }
  };

  const dismissMergeCandidate = async (
    candidateSnapshotIds: string[],
    actionVersion?: string,
  ) => {
    if (!selected || !candidateSnapshotIds.length) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const shadow = isShadowSubject(selected);
      if (shadow) {
        await dismissRealShadowMergeCandidate(
          selected.realShadowId ?? selected.inquiryId,
          candidateSnapshotIds,
          `real-shadow-merge-dismiss-${selected.inquiryId}-${candidateSnapshotIds.slice().sort().join("-")}`,
        );
      } else {
        const capability = (
          workbench?.unifiedBusinessView as
            | TriageBusinessWorkbenchDetail
            | undefined
        )?.primaryActionCapability;
        const effectiveVersion = actionVersion ?? capability?.actionVersion;
        if (capability?.type !== "MERGE" || !effectiveVersion)
          throw new Error("当前合并建议已变化，请刷新后重试");
        await dismissFormalInquiryMergeCandidate(
          selected.inquiryId,
          {
            candidateInquiryIds: candidateSnapshotIds,
            actionVersion: effectiveVersion,
          },
          `formal-inquiry-merge-dismiss-${selected.inquiryId}-${candidateSnapshotIds.slice().sort().join("-")}`,
        );
      }
      const selectedSubject = subjectRefOf(selected);
      const refreshed = await loadTriageBusinessWorkbench(selectedSubject);
      unifiedWorkbenchCache.current.set(
        `${selectedSubject.kind}:${selectedSubject.id}`,
        refreshed,
      );
      setWorkbench((current) =>
        current ? { ...current, unifiedBusinessView: refreshed } : current,
      );
      setNotice("已选择分别处理；当前合并建议不再重复提醒。");
      await refresh();
    } catch (cause) {
      setError(describeActionError(cause, "分别处理记录失败"));
    } finally {
      setBusy(false);
    }
  };

  const closeWorkbench = () => {
    setWorkbench(undefined);
    setSelected(undefined);
    setDistributionResult(undefined);
    setQueueCompleted(false);
    const url = new URL(window.location.href);
    url.searchParams.set("view", "inquiry-center");
    url.searchParams.delete("selected");
    url.searchParams.delete("triage");
    url.searchParams.delete("peek");
    window.history.replaceState(null, "", url);
  };

  useEffect(() => {
    if (!selected) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.key !== "Escape") return;
      const secondaryOverlayOpen =
        new URLSearchParams(window.location.search).has("peek") ||
        document.querySelector("[data-overlay-layer='secondary-peek']") !==
          null ||
        document.querySelector("[data-overlay-layer='merge-workspace']") !==
          null ||
        document.querySelector("[data-overlay-layer='action-confirmation']") !==
          null;
      if (!secondaryOverlayOpen) closeWorkbench();
    };
    window.addEventListener("keydown", handleKey, { capture: true });
    return () => {
      window.removeEventListener("keydown", handleKey, { capture: true });
    };
  }, [selected?.inquiryId, selected?.subjectRef?.kind]);

  const openNextInquiry = () => {
    if (!selected) return;
    const queue = data?.items ?? [];
    const currentIndex = queue.findIndex(
      (entry) => entry.inquiryId === selected.inquiryId,
    );
    const next = queue[currentIndex + 1] ?? queue[0];
    if (next && next.inquiryId !== selected.inquiryId) {
      setDistributionResult(undefined);
      void openWorkbench(next);
    } else {
      closeWorkbench();
    }
    void refresh();
  };

  const openQueuePrimaryAction = async (item: TriageInquiry) => {
    await openWorkbench(item);
  };

  const openListHandledElsewhere = (item: TriageInquiry) => {
    setError("");
    setNotice("");
    setListHandledElsewhereReason("HANDLED_IN_GMAIL");
    setListHandledElsewhere(item);
  };

  const confirmListHandledElsewhere = async () => {
    const target = listHandledElsewhere;
    if (!target) return;
    const reasonText = handledElsewhereReasons[listHandledElsewhereReason];
    setListHandledElsewhere(null);
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await decideTriage(
        target.inquiryId,
        {
          expectedVersion: target.lockVersion,
          emailCategory: target.emailClassification.category,
          customerTier: listCustomerTier(target.customerTier),
          disposition: "HANDLED_ELSEWHERE",
          reason: reasonText,
          confirmedMessageNature: "NEW_INQUIRY",
          confirmedFields: {
            source: "EVITACHEM_GMAIL_HUMAN_CONFIRMATION",
            synthetic: false,
          },
        },
        `triage-decision-${target.inquiryId}-${crypto.randomUUID()}`,
      );
      setNotice(`已在别处处理，已关闭：${target.customerCandidate}`);
      await refresh();
    } catch (cause) {
      setError(describeActionError(cause, "标记已在别处处理失败。"));
    } finally {
      setBusy(false);
    }
  };

  const runSelectedAgent = async () => {
    if (!selected || !isShadowSubject(selected)) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await runRealShadowAgent(selected.realShadowId ?? selected.inquiryId);
      const selectedSubject = subjectRefOf(selected);
      const detail = await loadTriageBusinessWorkbench(selectedSubject);
      unifiedWorkbenchCache.current.set(
        `${selectedSubject.kind}:${selectedSubject.id}`,
        detail,
      );
      setWorkbench({
        workbenchExperience: "UNIFIED_BUSINESS_V1",
        unifiedBusinessView: detail,
      });
      setNotice("DeepSeek 判断已提交，结果完成后会自动更新。");
      await refresh();
    } catch (cause) {
      setError(describeActionError(cause, "AI 判断提交失败"));
    } finally {
      setBusy(false);
    }
  };

  const loadSelectedOriginalMail = async () => {
    if (!selected) return null;
    const subject = subjectRefOf(selected);
    const sourceId = selected.realShadowId ?? subject.id ?? selected.inquiryId;
    return loadOriginalMailMessage(
      sourceId,
      subject.kind === "FORMAL_INQUIRY" ? "FORMAL_INQUIRY" : "SHADOW_SNAPSHOT",
    );
  };

  const loadOriginalMailForInquiry = async (inquiryId: string) => {
    if (!selected) return null;
    const subject = subjectRefOf(selected);
    return loadOriginalMailMessage(
      inquiryId,
      subject.kind === "FORMAL_INQUIRY" ? "FORMAL_INQUIRY" : "SHADOW_SNAPSHOT",
    );
  };

  const saveSelectedProductFields = async (
    fields: ConfirmedInquiryFields,
    reason?: string,
  ) => {
    if (!selected || !workbench) return;
    const detail =
      workbench.unifiedBusinessView as TriageBusinessWorkbenchDetail;
    const productId = detail.products?.[0]?.id;
    if (!productId || !detail.lifecycle?.detailVersion)
      throw new Error("当前产品字段缺少稳定版本，请刷新后重试。");
    const saved = isShadowSubject(selected)
      ? await saveRealShadowProductFields(
          selected.realShadowId ?? selected.inquiryId,
          {
            productId,
            confirmedFields: fields,
            expectedDetailVersion: detail.lifecycle.detailVersion,
            ...(reason ? { reason } : {}),
          },
        )
      : await saveTriageProductFields(selected.inquiryId, {
          productId,
          confirmedFields: fields,
          expectedDetailVersion: detail.lifecycle.detailVersion,
          ...(reason ? { reason } : {}),
        });
    const refreshed = await loadTriageBusinessWorkbench(subjectRefOf(selected));
    unifiedWorkbenchCache.current.set(
      `${subjectRefOf(selected).kind}:${subjectRefOf(selected).id}`,
      refreshed,
    );
    setWorkbench((current) =>
      current ? { ...current, unifiedBusinessView: refreshed } : current,
    );
    setNotice(
      String(
        (saved as { message?: unknown } | undefined)?.message ??
          "产品信息已保存，当前分发将使用人工确认值。",
      ),
    );
  };

  const saveSelectedCustomerProfile = async (
    profile: CustomerProfileDraft,
    reason?: string,
  ) => {
    if (!selected || !workbench) return;
    const detail =
      workbench.unifiedBusinessView as TriageBusinessWorkbenchDetail;
    if (!detail.lifecycle?.detailVersion)
      throw new Error("当前客户画像缺少稳定版本，请刷新后重试。");
    const saved = isShadowSubject(selected)
      ? await saveRealShadowCustomerProfile(
          selected.realShadowId ?? selected.inquiryId,
          {
            organizationName: profile.organizationName,
            country: profile.country,
            website: profile.website,
            customerGrade: profile.customerGrade,
            profileNarrative: profile.profileNarrative,
            institutionIntroduction: profile.institutionIntroduction,
            industryResearchCapability: profile.industryResearchCapability,
            handlingRecommendation: profile.handlingRecommendation,
            domainVerificationDecision: profile.domainVerificationDecision,
            expectedDetailVersion: detail.lifecycle.detailVersion,
            ...(reason ? { reason } : {}),
          },
        )
      : await saveTriageCustomerProfile(selected.inquiryId, {
          organizationName: profile.organizationName,
          country: profile.country,
          website: profile.website,
          customerGrade: profile.customerGrade,
          profileNarrative: profile.profileNarrative,
          institutionIntroduction: profile.institutionIntroduction,
          industryResearchCapability: profile.industryResearchCapability,
          handlingRecommendation: profile.handlingRecommendation,
          domainVerificationDecision: profile.domainVerificationDecision,
          expectedDetailVersion: detail.lifecycle.detailVersion,
          ...(reason ? { reason } : {}),
        });
    const refreshed = await loadTriageBusinessWorkbench(subjectRefOf(selected));
    unifiedWorkbenchCache.current.set(
      `${subjectRefOf(selected).kind}:${subjectRefOf(selected).id}`,
      refreshed,
    );
    setWorkbench((current) =>
      current ? { ...current, unifiedBusinessView: refreshed } : current,
    );
    setNotice(
      String(
        (saved as { message?: unknown } | undefined)?.message ??
          "客户画像已保存，人工修正已留痕。",
      ),
    );
  };

  /**
   * Releases a permanent domain-risk mark. The API restricts this to Workspace administrators; a
   * non-admin operator sees the server's refusal instead of a silent no-op.
   */
  const clearSelectedDomainRisk = async (domain: string) => {
    if (!selected) return;
    setError("");
    try {
      const saved = await clearTriageDomainRisk(domain);
      const refreshed = await loadTriageBusinessWorkbench(
        subjectRefOf(selected),
      );
      unifiedWorkbenchCache.current.set(
        `${subjectRefOf(selected).kind}:${subjectRefOf(selected).id}`,
        refreshed,
      );
      setWorkbench((current) =>
        current ? { ...current, unifiedBusinessView: refreshed } : current,
      );
      if (saved.cleared) {
        const normalized = domain.trim().toLowerCase();
        setReleasedDomainMarks((current) => ({
          ...current,
          [normalized]: new Date().toISOString(),
        }));
      }
      setNotice(
        saved.cleared
          ? `已解除 ${domain} 的永久域名标记；下次分析会重新核查该域名。本询单的人工确认记录保留。`
          : `${domain} 当前没有可解除的人工标记。`,
      );
    } catch (cause) {
      setError(describeActionError(cause, "解除域名风险标记失败。"));
    }
  };

  const unifiedBusinessExperience =
    workbench?.workbenchExperience === "UNIFIED_BUSINESS_V1" &&
    Boolean(workbench.unifiedBusinessView);
  const decisionWorkspace =
    workbench && selected ? (
      <TriageDecisionWorkspace
        workspaceCode={workspaceCode}
        item={selected}
        workbench={workbench}
        disposition={disposition}
        setDisposition={setDisposition}
        customerTier={customerTier}
        setCustomerTier={setCustomerTier}
        emailCategory={emailCategory}
        setEmailCategory={setEmailCategory}
        existingCaseId={existingCaseId}
        otherInquiryId={otherInquiryId}
        setExistingCaseId={setExistingCaseId}
        setOtherInquiryId={setOtherInquiryId}
        distributionResult={distributionResult}
        reason={reason}
        setReason={setReason}
        salesMembers={salesMembers}
        sourcingMembers={sourcingMembers}
        salesUserId={salesUserId}
        sourcingUserId={sourcingUserId}
        setSalesUserId={setSalesUserId}
        setSourcingUserId={setSourcingUserId}
        busy={busy}
        operationState={operationState}
        mergeResult={mergeResult}
        realDistributionResult={realDistributionResult}
        error={error || agentAnalysisError}
        notice={notice}
        onSubmit={(
          nextSalesUserId,
          nextSourcingUserId,
          nextSourcingStrategy,
          confirmedFields,
          confirmedMessageNature,
          nextCcSalesArchive,
        ) =>
          void submitDecision(
            nextSalesUserId,
            nextSourcingUserId,
            nextSourcingStrategy,
            confirmedFields,
            undefined,
            confirmedMessageNature,
            undefined,
            nextCcSalesArchive,
          )
        }
        onMarkNotInquiry={(suppressSender) =>
          void submitDecision(
            undefined,
            undefined,
            undefined,
            undefined,
            "MARK_INVALID",
            undefined,
            undefined,
            suppressSender,
          )
        }
        onMarkInvalidInquiry={(invalidReason, suppressSender) =>
          void submitDecision(
            undefined,
            undefined,
            undefined,
            undefined,
            "MARK_INVALID_INQUIRY",
            "NEW_INQUIRY",
            invalidReason,
            suppressSender,
          )
        }
        onMarkHandledElsewhere={(handledElsewhereReason) =>
          void submitDecision(
            undefined,
            undefined,
            undefined,
            undefined,
            "HANDLED_ELSEWHERE",
            "NEW_INQUIRY",
            handledElsewhereReason,
          )
        }
        onClose={closeWorkbench}
        onNext={openNextInquiry}
        onRetry={() => selected && void openWorkbench(selected, "none")}
        onRunAgent={
          isShadowSubject(selected) ? () => void runSelectedAgent() : undefined
        }
        onLoadOriginalMail={
          unifiedBusinessExperience
            ? () => loadSelectedOriginalMail()
            : undefined
        }
        onLoadOriginalMailForInquiry={
          unifiedBusinessExperience
            ? (inquiryId) => loadOriginalMailForInquiry(inquiryId)
            : undefined
        }
        onSaveProductFields={(fields, reason) =>
          saveSelectedProductFields(fields, reason)
        }
        onSaveCustomerProfile={(profile, reason) =>
          saveSelectedCustomerProfile(profile, reason)
        }
        onClearDomainRisk={(domain) => clearSelectedDomainRisk(domain)}
        releasedDomainMarks={releasedDomainMarks}
        onMerge={mergeRelated}
        onDismissMerge={dismissMergeCandidate}
        unifiedBusiness={unifiedBusinessExperience}
      />
    ) : null;
  if (decisionWorkspace && !unifiedBusinessExperience) return decisionWorkspace;

  const overlayRoot = document.getElementById("app-overlay-root");
  if (!overlayRoot) throw new Error("缺少应用 Overlay Root");
  const workbenchOverlay = createPortal(
    <>
      {selected ? (
        <button
          type="button"
          className="triage-drawer-scrim"
          data-overlay-layer="workbench-backdrop"
          aria-label="关闭当前询单工作台遮罩"
          onClick={closeWorkbench}
        />
      ) : null}
      <TriageWorkbenchShell open={Boolean(selected)} onClose={closeWorkbench}>
        <section ref={drawerRef} className="triage-workbench-shell-content">
          {decisionWorkspace}
          {selected &&
          !decisionWorkspace &&
          operationState === "LOAD_FAILED" ? (
            <div className="real-workbench-load-error" role="alert">
              <strong>询单详情加载失败</strong>
              <p>{error || "当前无法读取询单详情，请重新加载。"}</p>
              <div>
                <button
                  type="button"
                  className="triage-primary-action"
                  onClick={() => void openWorkbench(selected, "none")}
                >
                  重新加载
                </button>
                <button type="button" onClick={closeWorkbench}>
                  返回队列
                </button>
              </div>
            </div>
          ) : null}
          {selected &&
          !decisionWorkspace &&
          operationState !== "LOAD_FAILED" ? (
            <div
              className="real-workbench-skeleton"
              aria-label="正在加载询单工作台"
            >
              <span />
              <span />
              <span />
              <p>正在加载当前询单…</p>
            </div>
          ) : null}
          {selected && queueCompleted ? (
            <div className="real-queue-complete" role="status">
              <strong>当前队列已处理完成</strong>
              <span>可以关闭工作台，或调整筛选查看其他询单。</span>
            </div>
          ) : null}
        </section>
      </TriageWorkbenchShell>
    </>,
    overlayRoot,
  );

  const listHandledElsewhereOverlay = createPortal(
    listHandledElsewhere ? (
      <div
        className="real-merge-confirm real-forward-confirm real-handled-elsewhere-confirm"
        data-overlay-layer="action-confirmation"
        role="dialog"
        aria-modal="true"
        aria-label="确认已在别处处理"
      >
        <strong>确认已在 Gmail/他处处理</strong>
        <p>
          询单 {listHandledElsewhere.customerCandidate}
          {listHandledElsewhere.mailSubject
            ? ` · ${listHandledElsewhere.mailSubject}`
            : ""}
        </p>
        <p>
          当前记录仍保留为客户询单，仅在 CRM
          内关闭；不会创建案件、分配或任务，也不会向客户或供应商发送邮件，Gmail
          邮件的已读状态不受影响。
        </p>
        <label>
          <span>处理去向</span>
          <select
            aria-label="处理去向"
            value={listHandledElsewhereReason}
            onChange={(event) =>
              setListHandledElsewhereReason(
                event.target.value as HandledElsewhereReason,
              )
            }
            disabled={busy}
          >
            {Object.entries(handledElsewhereReasons).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <div>
          <button
            type="button"
            className="real-secondary-action"
            onClick={() => setListHandledElsewhere(null)}
            disabled={busy}
          >
            取消
          </button>
          <button
            type="button"
            className="real-handled-elsewhere-confirm-action"
            onClick={() => void confirmListHandledElsewhere()}
            disabled={busy}
          >
            确认已在别处处理
          </button>
        </div>
      </div>
    ) : null,
    overlayRoot,
  );

  /**
   * Pending work that has been fully cleared is still pending work: the tab stays put and
   * says so, instead of leaving the operator with a blank list or a silent jump to 已处理.
   */
  const queueEmptyMessage =
    statusFilter === "PROCESSED"
      ? "当前范围没有已处理的询单。"
      : statusFilter === "NO_ACTION"
        ? "当前范围没有不需要处理的邮件。"
        : data && data.statuses.processed > 0
          ? `暂无待处理询单，已处理的 ${data.statuses.processed} 条可在「已处理」中查看。`
          : "当前范围没有符合条件的询单。";

  return (
    <main className="triage-workspace" aria-label="询单分诊与分发工作台">
      {listHandledElsewhereOverlay}
      <SenderSuppressionPanel
        open={suppressionOpen}
        onClose={() => setSuppressionOpen(false)}
      />
      <header className="triage-compact-header">
        <div>
          <h1>{exceptions ? "异常与复核" : "询单中心"}</h1>
          <strong>
            {data ? `${data.statuses.pending} 待处理` : "正在加载"}
          </strong>
        </div>
        <div className="triage-compact-header-actions">
          <span>询单工作台</span>
          <button
            type="button"
            className="real-secondary-action"
            onClick={() => setSuppressionOpen(true)}
          >
            不再合作名单
          </button>
        </div>
      </header>
      <div className="triage-toolbar">
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") void refresh();
          }}
          placeholder="搜索客户、邮箱、产品或 CAS"
          aria-label="搜索询单"
        />
        <div className="triage-filter-anchor">
          <button
            type="button"
            className={filterOpen ? "is-active" : ""}
            onClick={() => setFilterOpen((current) => !current)}
            aria-expanded={filterOpen}
          >
            筛选
            {[
              category,
              exceptionType,
              gradeFilter,
              countryFilter,
              natureFilter,
            ].filter(Boolean).length +
              (statusFilter !== "PENDING" ? 1 : 0) >
            0
              ? ` · ${
                  [
                    category,
                    exceptionType,
                    gradeFilter,
                    countryFilter,
                    natureFilter,
                  ].filter(Boolean).length +
                  (statusFilter !== "PENDING" ? 1 : 0)
                }`
              : ""}
          </button>
          {filterOpen ? (
            <section className="triage-filter-popover" aria-label="询单筛选">
              <label>
                邮箱类型
                <select
                  value={category ?? ""}
                  onChange={(event) =>
                    setCategory(
                      (event.target.value || undefined) as
                        | TriageCategory
                        | undefined,
                    )
                  }
                >
                  <option value="">全部</option>
                  <option value="CORPORATE">企业</option>
                  <option value="ACADEMIC_INSTITUTIONAL">学校 / 机构</option>
                  <option value="PERSONAL">个人</option>
                </select>
              </label>
              <label>
                状态
                <select
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(
                      event.target.value as
                        | "PENDING"
                        | "PROCESSED"
                        | "NO_ACTION",
                    )
                  }
                >
                  <option value="PENDING">待处理</option>
                  <option value="PROCESSED">已处理</option>
                </select>
              </label>
              <label>
                异常
                <select
                  value={exceptionType ?? ""}
                  onChange={(event) =>
                    setExceptionType(
                      (event.target.value || undefined) as
                        | TriageExceptionType
                        | undefined,
                    )
                  }
                >
                  <option value="">全部</option>
                  <option value="RISK_REVIEW_REQUIRED">
                    风险 / 国家 / 运输
                  </option>
                  <option value="DUPLICATE_CONFLICT">重复</option>
                  <option value="MESSAGE_NATURE_UNKNOWN">邮件性质</option>
                  <option value="EMAIL_TYPE_UNKNOWN">邮箱类型</option>
                  <option value="CUSTOMER_IDENTITY_UNKNOWN">客户身份</option>
                  <option value="CUSTOMER_IDENTITY_CONFLICT">
                    客户身份冲突
                  </option>
                  <option value="HUMAN_REVIEW_REQUIRED">人工业务复核</option>
                  <option value="AI_JUDGMENT_FAILED">AI 判断失败</option>
                  <option value="AI_JUDGMENT_REVIEW">AI 判断待复核</option>
                  <option value="PRODUCT_INFORMATION_MISSING">
                    产品信息缺失
                  </option>
                  <option value="QUANTITY_INFORMATION_MISSING">
                    数量信息缺失
                  </option>
                  <option value="EXISTING_CASE_CONFLICT">案件关联冲突</option>
                  <option value="ROUTING_FAILED">路由失败</option>
                  <option value="ASSIGNMENT_FAILED">分配失败</option>
                </select>
              </label>
              <label>
                客户等级
                <select
                  value={gradeFilter}
                  onChange={(event) => setGradeFilter(event.target.value)}
                >
                  <option value="">全部</option>
                  {["A", "B", "C", "D", "E"].map((grade) => (
                    <option key={grade} value={grade}>
                      {grade} 类
                    </option>
                  ))}
                </select>
              </label>
              <label>
                国家
                <input
                  value={countryFilter}
                  onChange={(event) => setCountryFilter(event.target.value)}
                  placeholder="搜索国家"
                />
              </label>
              <label>
                邮件判断结果
                <select
                  value={natureFilter}
                  onChange={(event) => setNatureFilter(event.target.value)}
                >
                  <option value="">全部</option>
                  {EMAIL_NATURE_FILTER_OPTIONS.map((nature) => (
                    <option key={nature} value={nature}>
                      {statusLabel(nature)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => {
                  setCategory(undefined);
                  setExceptionType(undefined);
                  setStatusFilter("PENDING");
                  setGradeFilter("");
                  setCountryFilter("");
                  setNatureFilter("");
                }}
              >
                清除筛选
              </button>
            </section>
          ) : null}
        </div>
        <select
          aria-label="询单排序"
          value={sortOrder}
          onChange={(event) =>
            setSortOrder(event.target.value as "NEWEST" | "OLDEST")
          }
        >
          <option value="NEWEST">最新优先</option>
          <option value="OLDEST">最早优先</option>
        </select>
        <button type="button" onClick={() => void refresh()} disabled={busy}>
          刷新
        </button>
      </div>
      <div className="triage-queue-segments" aria-label="询单队列视图">
        <div role="group" aria-label="处理状态">
          <button
            type="button"
            aria-pressed={statusFilter === "PENDING"}
            onClick={() => setStatusFilter("PENDING")}
          >
            待处理 {data ? data.statuses.pending : "…"}
          </button>
          <button
            type="button"
            aria-pressed={statusFilter === "PROCESSED"}
            onClick={() => setStatusFilter("PROCESSED")}
          >
            已处理 {data ? data.statuses.processed : "…"}
          </button>
        </div>
        <div role="group" aria-label="邮箱类别">
          <button
            type="button"
            aria-pressed={!category}
            onClick={() => setCategory(undefined)}
          >
            全部邮箱
          </button>
          {(
            [
              ["PERSONAL", "个人邮箱"],
              ["ACADEMIC_INSTITUTIONAL", "学校 / 机构"],
              ["CORPORATE", "企业邮箱"],
            ] as const
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={category === value}
              onClick={() => setCategory(value)}
            >
              {label} {data ? data.counts[value] : "…"}
            </button>
          ))}
        </div>
      </div>
      {notice ? <div className="triage-feedback success">{notice}</div> : null}
      {error || agentAnalysisError ? (
        <div className="triage-feedback error">
          {error || agentAnalysisError}
        </div>
      ) : null}
      {pilotMode === "REAL_SHADOW" ? (
        <section className="triage-inquiry-surface" aria-label="询单连续工作台">
          {successReceipts.length ? (
            <div className="real-success-receipts" aria-label="最近完成的操作">
              {successReceipts.map((receipt) => (
                <details key={receipt.id}>
                  <summary>刚刚完成：{receipt.title}</summary>
                  <div>
                    <span>
                      案件：
                      {receipt.caseNumber ??
                        receipt.caseId?.slice(0, 8) ??
                        "未创建"}
                    </span>
                    <span>销售：{receipt.sales}</span>
                    <span>查货：{receipt.sourcing}</span>
                    <span>任务：{receipt.taskCount} 个</span>
                    {receipt.caseId ? (
                      <button
                        type="button"
                        onClick={() =>
                          window.location.assign(
                            `/?view=inquiry-center&case=${encodeURIComponent(receipt.caseId!)}`,
                          )
                        }
                      >
                        查看案件
                      </button>
                    ) : null}
                  </div>
                </details>
              ))}
            </div>
          ) : null}
          <div
            className="real-inquiry-queue"
            ref={queueScrollRef}
            aria-label="询单队列"
          >
            <header className="real-inquiry-list-header" aria-hidden="true">
              <span>{exceptions ? "询单 / 客户" : "客户"}</span>
              <span>{exceptions ? "产品 / 时间" : "需求"}</span>
              <span>{exceptions ? "异常 / SLA" : "建议处置"}</span>
              <span>操作</span>
            </header>
            <div className="real-inquiry-queue-list">
              {data?.items.map((item) => {
                const realItem = realShadowItems.find(
                  (entry) => entry.id === item.inquiryId,
                );
                const displayProduct = queueProductDisplay(
                  item.product,
                  item.cas,
                );
                const displayQuantity = queueQuantityDisplay(item.quantity);
                const submittedAtIso =
                  item.submittedAt ?? realItem?.sourceTimestamp ?? null;
                const submittedAtLabel =
                  formatInquirySubmittedAt(submittedAtIso);
                const primaryAction =
                  item.primaryActionCapability ??
                  realItem?.primaryActionCapability;
                const exceptionTypes = realItem
                  ? realExceptionTypes(realItem)
                  : item.exceptionTypes;
                const communicationOnly =
                  item.businessSemantics?.recordKind === "COMMUNICATION_ONLY";
                const productRiskApplicable =
                  item.businessSemantics?.productRiskApplicable !== false;
                const inquiryReviewCandidate =
                  item.dataOrigin === "REAL_GMAIL" &&
                  item.businessSemantics?.recordKind === "BUSINESS_REVIEW" &&
                  ["GENERAL_QUESTION", "UNKNOWN"].includes(
                    item.businessSemantics.messageNature,
                  );
                const queueActionLabel =
                  primaryAction?.type === "VIEW_RESULT"
                    ? "查看处理结果"
                    : primaryAction?.type === "NONE"
                      ? "查看通信记录"
                      : primaryAction?.type === "MERGE"
                        ? "查看待合并询单"
                        : primaryAction?.type === "GROUP_FORWARD"
                          ? "打开联合询单"
                          : "打开分诊台";
                /**
                 * The queue only prints an actionable disposition. A clean risk check is the
                 * default outcome, so those rows leave 建议处置 empty instead of repeating a
                 * no-risk conclusion on every line.
                 */
                const dispositionSummary = inquiryReviewCandidate
                  ? "需确认是否为客户询单"
                  : !productRiskApplicable
                    ? (item.businessSemantics?.queueBucketLabel ?? "")
                    : realItem?.duplicateProfile.classification ===
                        "EXACT_DUPLICATE"
                      ? "重复询单 · 需要复核"
                      : realItem?.duplicateProfile.mergeSuggestion
                        ? `合并候选 · ${realItem.duplicateProfile.mergeSuggestion.inquiryCount} 条`
                        : realItem?.duplicateProfile.requiresReview
                          ? "询单关系 · 需要复核"
                          : realItem?.triageDecisionProfile
                            ? triageDecisionLabel(
                                realItem.triageDecisionProfile.overallStatus,
                                realItem.triageDecisionProfile.businessState,
                              )
                            : realItem?.productRiskProfile
                              ? queueRiskSummary(
                                  realItem.productRiskProfile,
                                  Boolean(displayProduct) &&
                                    realItem.inquiryDetailProfile
                                      .productIdentityResolved,
                                )
                              : item.risk === "CLEAR"
                                ? ""
                                : statusLabel(item.risk);
                const queueDecisionLabel =
                  item.status === "PROCESSED"
                    ? dispositionSummary
                      ? `已处理 · ${dispositionSummary}`
                      : "已处理"
                    : dispositionSummary;
                return (
                  <div
                    className={`${selected?.inquiryId === item.inquiryId ? "real-inquiry-row is-selected" : "real-inquiry-row"}${exceptions ? " is-exception-row" : ""}`}
                    key={item.inquiryId}
                  >
                    <button
                      type="button"
                      className="real-inquiry-row-open"
                      aria-label={`打开询单：${item.mailSubject ?? item.customerCandidate} · ${displayProduct ?? "产品待确认"}`}
                      aria-pressed={selected?.inquiryId === item.inquiryId}
                      onClick={() => void openWorkbench(item)}
                    >
                      {item.dataOrigin === "REAL_GMAIL" ? (
                        <span className="real-inquiry-row-subject">
                          <small>邮件主题</small>
                          <strong title={item.mailSubject ?? "（无主题）"}>
                            {item.mailSubject ?? "（无主题）"}
                          </strong>
                        </span>
                      ) : null}
                      <span className="real-inquiry-row-customer">
                        <strong>
                          {exceptions
                            ? item.inquiryNumber
                            : item.customerCandidate}
                        </strong>
                        {item.dataOrigin === "REAL_GMAIL" ? (
                          <small className="real-inquiry-row-meta">
                            来源：真实 Gmail ·{" "}
                            {statusLabel(item.emailNature ?? "UNKNOWN")}
                          </small>
                        ) : null}
                        <small>
                          {exceptions ? `${item.customerCandidate} · ` : ""}
                          {item.mergedInquiryCount
                            ? `合并组 · ${item.mergedInquiryCount} 条询单 · `
                            : ""}
                          {item.businessSemantics &&
                          !item.businessSemantics.customerProfileApplicable
                            ? item.businessSemantics.identityLabel
                            : `${customerGradeLabel(item.customerTier)} · ${
                                item.customerCountry ??
                                realItem?.customerBusinessProfile.country ??
                                "国家待确认"
                              }`}
                        </small>
                        {item.dataOrigin !== "REAL_GMAIL" ? (
                          <small className="real-inquiry-row-meta">
                            邮箱：
                            {CATEGORY_LABELS[item.emailClassification.category]}
                          </small>
                        ) : null}
                      </span>
                      <span className="real-inquiry-row-request">
                        <strong>
                          {communicationOnly
                            ? item.businessSemantics?.messageNatureLabel
                            : (displayProduct ?? "产品待确认")}
                        </strong>
                        <small>
                          {communicationOnly
                            ? item.businessSemantics?.businessImpactLabel
                            : item.mergedProductCount
                              ? `${item.mergedProductCount} 个产品 · 共享 1 个案件`
                              : (displayQuantity ?? "数量待确认")}
                        </small>
                        <small className="real-inquiry-row-meta real-inquiry-row-time">
                          询单时间{" "}
                          {submittedAtLabel ? (
                            <time
                              dateTime={submittedAtIso ?? undefined}
                              title={
                                submittedAtIso
                                  ? `${submittedAtLabel} · ${inquiryAge(submittedAtIso)}前`
                                  : submittedAtLabel
                              }
                            >
                              {submittedAtLabel}
                            </time>
                          ) : (
                            "时间待确认"
                          )}
                        </small>
                      </span>
                      <span className="real-inquiry-row-decision">
                        {exceptions ? (
                          <>
                            <strong>
                              {exceptionTypes
                                .map((type) => EXCEPTION_LABELS[type])
                                .join(" · ") || "待复核"}
                            </strong>
                            <small className="real-inquiry-row-meta">
                              严重程度：{exceptionSeverity(exceptionTypes)} ·
                              当前负责人：分诊队列 · SLA：
                              {realItem
                                ? inquiryAge(realItem.sourceTimestamp)
                                : "待确认"}
                            </small>
                          </>
                        ) : (
                          queueDecisionLabel
                        )}
                      </span>
                    </button>
                    {selected?.inquiryId === item.inquiryId ? (
                      <span className="real-inquiry-row-action is-open">
                        当前查看
                      </span>
                    ) : (
                      <span className="real-inquiry-row-actions">
                        <button
                          type="button"
                          aria-label={`${queueActionLabel}：${item.customerCandidate}`}
                          className={
                            primaryAction?.allowed &&
                            primaryAction.type !== "VIEW_RESULT"
                              ? "real-inquiry-row-action is-primary"
                              : "real-inquiry-row-action"
                          }
                          title={primaryAction?.disabledReason ?? undefined}
                          onClick={() => void openQueuePrimaryAction(item)}
                        >
                          {queueActionLabel}
                        </button>
                        {listHandledElsewhereEligible(item) ? (
                          <button
                            type="button"
                            aria-label={`已在别处处理：${item.customerCandidate}`}
                            className="real-inquiry-row-action is-handled-elsewhere"
                            onClick={() => openListHandledElsewhere(item)}
                            disabled={busy}
                          >
                            已在别处处理
                          </button>
                        ) : null}
                      </span>
                    )}
                  </div>
                );
              })}
              {!busy && !data?.items.length ? (
                <div className="triage-empty">{queueEmptyMessage}</div>
              ) : null}
            </div>
          </div>
        </section>
      ) : (
        <section className="triage-table-card">
          <table>
            <thead>
              <tr>
                <th>询单</th>
                <th>客户</th>
                <th>需求</th>
                <th>产品风险</th>
                <th>近期历史</th>
                <th>建议处理</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((item) => (
                <tr
                  key={item.inquiryId}
                  onClick={() => void openWorkbench(item)}
                >
                  <td>
                    <strong>{item.inquiryNumber}</strong>
                    <small>
                      {item.sourceSite.name} · {item.sourceDomain}
                    </small>
                  </td>
                  <td>
                    <strong>{item.customerCandidate}</strong>
                    <small>
                      {item.customerTier && item.customerTier !== "PENDING"
                        ? `${item.customerTier} 类客户`
                        : "等级待确认"}{" "}
                      ·{" "}
                      {realShadowItems.find(
                        (entry) => entry.id === item.inquiryId,
                      )?.customerBusinessProfile.country ??
                        CATEGORY_LABELS[item.emailClassification.category]}
                    </small>
                  </td>
                  <td>
                    <strong>{item.product ?? "产品待确认"}</strong>
                    <small>
                      {item.cas ? `CAS ${item.cas}` : "CAS 由内部目录核对"} ·{" "}
                      {item.quantity ?? "数量待确认"}
                    </small>
                  </td>
                  <td>
                    <strong>
                      {realShadowItems.find(
                        (entry) => entry.id === item.inquiryId,
                      )?.productRiskProfile.overallLabel ??
                        statusLabel(item.risk)}
                    </strong>
                    <small>
                      {realShadowItems.find(
                        (entry) => entry.id === item.inquiryId,
                      )?.productRiskProfile.transportSummary ??
                        "打开查看风险详情"}
                    </small>
                  </td>
                  <td>
                    <small>
                      {item.historyConclusion ?? "当前 CRM 未匹配到可复用报价"}
                    </small>
                  </td>
                  <td>
                    <strong>
                      {item.recommendation?.headline ?? "打开判断包"}
                    </strong>
                    <small>
                      {item.recommendation?.targetLabel ??
                        "具体对象将在工作台中展示"}
                    </small>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        void openWorkbench(item);
                      }}
                    >
                      查看建议
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!busy && !data?.items.length ? (
            <div className="triage-empty">{queueEmptyMessage}</div>
          ) : null}
        </section>
      )}
      {workbenchOverlay}
    </main>
  );
}

function TriageDecisionWorkspace({
  workspaceCode,
  item,
  workbench,
  disposition,
  setDisposition,
  customerTier,
  setCustomerTier,
  emailCategory,
  setEmailCategory,
  existingCaseId,
  otherInquiryId,
  setExistingCaseId,
  setOtherInquiryId,
  distributionResult,
  reason,
  setReason,
  salesMembers,
  sourcingMembers,
  salesUserId,
  sourcingUserId,
  setSalesUserId,
  setSourcingUserId,
  busy,
  operationState,
  mergeResult,
  realDistributionResult,
  error,
  notice,
  onSubmit,
  onMarkNotInquiry,
  onMarkInvalidInquiry,
  onMarkHandledElsewhere,
  onClose,
  onNext,
  onRetry,
  onRunAgent,
  onMerge,
  onDismissMerge,
  onLoadOriginalMail,
  onLoadOriginalMailForInquiry,
  onSaveProductFields,
  onSaveCustomerProfile,
  onClearDomainRisk,
  releasedDomainMarks,
  unifiedBusiness = false,
}: {
  workspaceCode?: string;
  item: TriageInquiry;
  workbench: Record<string, unknown>;
  disposition: Disposition;
  setDisposition: (value: Disposition) => void;
  customerTier: string;
  setCustomerTier: (value: string) => void;
  emailCategory: TriageCategory;
  setEmailCategory: (value: TriageCategory) => void;
  existingCaseId: string;
  otherInquiryId: string;
  setExistingCaseId: (value: string) => void;
  setOtherInquiryId: (value: string) => void;
  distributionResult?: Record<string, unknown>;
  reason: string;
  setReason: (value: string) => void;
  salesMembers: WorkforceMembership[];
  sourcingMembers: WorkforceMembership[];
  salesUserId: string;
  sourcingUserId: string;
  setSalesUserId: (value: string) => void;
  setSourcingUserId: (value: string) => void;
  busy: boolean;
  operationState: WorkbenchOperationState;
  mergeResult?: RealShadowMergeResult;
  realDistributionResult?:
    | RealShadowDistributionResult
    | RealShadowGroupDistributionResult
    | FormalInquiryDistributionResult;
  error: string;
  notice: string;
  onSubmit: (
    salesUserId?: string,
    sourcingUserId?: string,
    sourcingStrategy?: HumanGmailSourcingStrategy,
    confirmedFields?: ConfirmedInquiryFields,
    confirmedMessageNature?: "NEW_INQUIRY" | "PURCHASE_ORDER",
    ccSalesArchive?: boolean,
  ) => void;
  onMarkNotInquiry?: (suppressSender: boolean) => void;
  onMarkInvalidInquiry?: (reason: string, suppressSender: boolean) => void;
  onMarkHandledElsewhere?: (reason: string) => void;
  onClose: () => void;
  onNext: () => void;
  onRetry: () => void;
  onRunAgent?: () => void;
  onMerge: (candidateSnapshotIds: string[], actionVersion?: string) => void;
  onDismissMerge: (candidateSnapshotIds: string[]) => void;
  onLoadOriginalMail?: () => Promise<
    import("./api").OriginalMailMessage | null
  >;
  onLoadOriginalMailForInquiry?: (
    inquiryId: string,
  ) => Promise<import("./api").OriginalMailMessage | null>;
  onSaveProductFields?: (
    fields: ConfirmedInquiryFields,
    reason?: string,
  ) => Promise<void>;
  onSaveCustomerProfile?: (
    profile: CustomerProfileDraft,
    reason?: string,
  ) => Promise<void>;
  onClearDomainRisk?: (domain: string) => Promise<void>;
  /** Domains whose permanent Workspace mark was released in this session, keyed by domain. */
  releasedDomainMarks?: Record<string, string>;
  unifiedBusiness?: boolean;
}) {
  if (unifiedBusiness) {
    return (
      <TriageBusinessWorkbench
        workspaceCode={workspaceCode}
        item={{
          anonymousReference: item.inquiryNumber,
          reviewStatus: item.status,
        }}
        detail={workbench.unifiedBusinessView as TriageBusinessWorkbenchDetail}
        busy={busy}
        operationState={operationState}
        mergeResult={mergeResult}
        distributionResult={realDistributionResult}
        error={error}
        notice={notice}
        onClose={onClose}
        onSubmit={onSubmit}
        onMarkNotInquiry={onMarkNotInquiry}
        onMarkInvalidInquiry={onMarkInvalidInquiry}
        onMarkHandledElsewhere={onMarkHandledElsewhere}
        onNext={onNext}
        onRetry={onRetry}
        onRunAgent={onRunAgent}
        onLoadOriginalMail={onLoadOriginalMail}
        onLoadOriginalMailForInquiry={onLoadOriginalMailForInquiry}
        onSaveProductFields={onSaveProductFields}
        onSaveCustomerProfile={onSaveCustomerProfile}
        onClearDomainRisk={onClearDomainRisk}
        releasedDomainMarks={releasedDomainMarks}
        onMerge={onMerge}
        onDismissMerge={onDismissMerge}
      />
    );
  }
  const realShadow = false;
  const inquiry = (workbench.inquiry ?? {}) as Record<string, unknown>;
  const classification = item.emailClassification;
  const recommendation = (workbench.recommendation ?? {}) as Record<
    string,
    unknown
  >;
  const history = Array.isArray(workbench.history) ? workbench.history : [];
  const candidates = Array.isArray(workbench.duplicateCandidates)
    ? workbench.duplicateCandidates
    : [];
  const dimensions = Array.isArray(workbench.judgmentDimensions)
    ? workbench.judgmentDimensions
    : [];
  const sourcingHistory = (workbench.sourcingHistory ?? {}) as Record<
    string,
    unknown
  >;
  const shadowAi = (workbench.shadowAi ?? {}) as Record<string, unknown>;
  const decisionPackage = (workbench.decisionPackage ?? {}) as Record<
    string,
    unknown
  >;
  const processingResult = (workbench.processingResult ?? {}) as Record<
    string,
    unknown
  >;
  const isProcessed = processingResult.status === "PROCESSED";
  const inquirySummary = (decisionPackage.inquirySummary ?? {}) as Record<
    string,
    unknown
  >;
  const customerDecision = (decisionPackage.customerDecision ?? {}) as Record<
    string,
    unknown
  >;
  const inquiryDecision = (decisionPackage.inquiryDecision ?? {}) as Record<
    string,
    unknown
  >;
  const riskAndHistory = (decisionPackage.riskAndHistory ?? {}) as Record<
    string,
    unknown
  >;
  const recommendedPackage = (
    isProcessed
      ? {
          confidence: "HIGH",
          headline: "已完成内部业务分发",
          summary: `已持久化案件 ${String(processingResult.caseNumber ?? processingResult.caseId ?? "")}、角色分配与产品任务。当前页面为只读结果态。`,
          blockingReasons: [],
          evidence: [],
        }
      : (decisionPackage.recommendedDisposition ?? recommendation)
  ) as Record<string, unknown>;
  const packageEvidence = Array.isArray(recommendedPackage.evidence)
    ? recommendedPackage.evidence
    : [];
  const missingInformation = Array.isArray(
    recommendedPackage.missingInformation,
  )
    ? recommendedPackage.missingInformation
    : [];
  const targetCandidates = Array.isArray(recommendedPackage.targetCandidates)
    ? recommendedPackage.targetCandidates
    : [];
  const fieldValidation = Array.isArray(workbench.fieldValidation)
    ? workbench.fieldValidation
    : [];
  return (
    <main className="triage-workspace triage-decision">
      <header className="triage-header">
        <div>
          <button type="button" className="triage-back" onClick={onClose}>
            ← 返回询单中心
          </button>
          <span className="triage-eyebrow">全屏分诊工作台</span>
          <h1>{item.inquiryNumber}</h1>
          <p>
            {item.sourceSite.name} · {CATEGORY_LABELS[classification.category]}{" "}
            · {String(inquirySummary.customer ?? item.customerCandidate)} ·{" "}
            {item.email}
          </p>
          <p className="triage-summary-line">
            {String(inquirySummary.product ?? item.product ?? "产品待确认")} ·{" "}
            {String(inquirySummary.cas ?? item.cas ?? "CAS 待确认")} ·{" "}
            {String(inquirySummary.quantity ?? item.quantity ?? "数量待确认")} ·{" "}
            {String(inquirySummary.country ?? "国家待确认")}
            {inquirySummary.sla
              ? ` · SLA ${new Date(String(inquirySummary.sla)).toLocaleString("zh-CN")}`
              : ""}
          </p>
        </div>
        <span className="triage-safety">
          {item.dataOrigin === "REAL_GMAIL"
            ? "真实 Gmail · 本地受控验证 · Gmail 只读 · 客户与供应商外发关闭"
            : realShadow
              ? "REAL DATA · READ ONLY SHADOW · 不改变真实业务"
              : "合成数据 · 仅供测试 · 不联系真实客户"}
        </span>
      </header>
      <div className="triage-decision-grid">
        <section className="triage-evidence triage-decision-content">
          <article className="triage-summary-section">
            <h2>客户判断</h2>
            <div className="decision-headline-row">
              <div>
                <strong>
                  {String(
                    customerDecision.headline ??
                      inquiry.companyName ??
                      "客户身份待确认",
                  )}
                </strong>
                <span>
                  {String(
                    customerDecision.organization ??
                      inquiry.companyName ??
                      "无可靠组织数据",
                  )}
                </span>
              </div>
              <span className="decision-badge">
                {String(customerDecision.tier ?? "UNKNOWN") === "UNKNOWN"
                  ? "等级待确认"
                  : `客户 ${String(customerDecision.tier)}`}
              </span>
            </div>
            <p>
              {String(customerDecision.email ?? item.email)} ·{" "}
              {String(inquirySummary.country ?? "国家待确认")}
            </p>
            <div className="decision-facts-inline">
              <span>身份可信度：{inquiry.companyName ? "高" : "待确认"}</span>
              <span>
                历史：
                {history.length
                  ? `${history.length} 条同邮箱询单`
                  : "无可靠历史数据"}
              </span>
            </div>
            <div className="triage-inline-actions">
              <button
                type="button"
                onClick={() =>
                  setCustomerTier(
                    customerDecision.tier
                      ? String(customerDecision.tier)
                      : "UNKNOWN",
                  )
                }
              >
                确认客户等级
              </button>
              <button
                type="button"
                onClick={() => window.alert("客户历史证据已在当前判断包中展开")}
              >
                查看匹配证据
              </button>
            </div>
          </article>
          <article className="triage-summary-section">
            <h2>询单判断</h2>
            <div className="decision-headline-row">
              <strong>
                质量{" "}
                {String(inquiryDecision.quality ?? "Q3") === "REAL_SHADOW"
                  ? "真实只读影子验证"
                  : String(inquiryDecision.quality ?? "Q3")}
              </strong>
              <span>
                {String(inquiryDecision.completeness ?? "0/8")} 字段完整
              </span>
            </div>
            {realShadow && fieldValidation.length ? (
              <div className="decision-checks decision-validation-states">
                {fieldValidation.map((entry, index) => {
                  const validation = entry as Record<string, unknown>;
                  const state = String(validation.state ?? "UNKNOWN");
                  return (
                    <span
                      key={index}
                      className={
                        state === "MISSING_BLOCKING" || state === "CONFLICT"
                          ? "is-missing"
                          : ""
                      }
                    >
                      <strong>{realFieldLabel(validation.field)}</strong>
                      {VALIDATION_STATE_LABELS[state] ?? "状态待确认"}
                      <small>
                        {Array.isArray(validation.evidence)
                          ? validation.evidence
                              .map(realEvidenceLabel)
                              .join("；")
                          : "无可靠证据"}
                      </small>
                    </span>
                  );
                })}
              </div>
            ) : (
              <div className="decision-checks">
                {["产品", "CAS", "数量", "单位", "企业邮箱", "公司"].map(
                  (label) => (
                    <span
                      key={label}
                      className={
                        label === "公司" && !inquiry.companyName
                          ? "is-missing"
                          : ""
                      }
                    >
                      {label === "公司" && !inquiry.companyName ? "•" : "✓"}{" "}
                      {label}
                    </span>
                  ),
                )}
              </div>
            )}
            {missingInformation.length ? (
              <div className="decision-missing">
                <strong>缺失信息</strong>
                {missingInformation.map((entry, index) => {
                  const item = entry as Record<string, unknown>;
                  return (
                    <span key={index}>
                      • {realFieldLabel(item.field)} ·{" "}
                      {item.blocking ? "阻塞分发" : "不阻塞普通查货"}
                    </span>
                  );
                })}
              </div>
            ) : (
              <p className="decision-ok">关键询单字段已基本齐全。</p>
            )}
          </article>
          <article className="triage-summary-section">
            <h2>风险与历史</h2>
            <div className="decision-headline-row">
              <strong>
                风险{" "}
                {String(
                  riskAndHistory.riskLevel ??
                    (item.risk === "CLEAR" ? "R0" : "待确认"),
                )}
              </strong>
              <span>
                {candidates.length
                  ? `重复候选 ${candidates.length} 个`
                  : "未发现开放重复"}
              </span>
            </div>
            <p>
              {realShadow
                ? realEvidenceSummary(
                    recommendedPackage.summary ?? recommendation.reason,
                  ) || "暂无可靠历史结论"
                : String(recommendation.reason ?? "暂无可靠历史结论")}
            </p>
            <div className="decision-evidence-list">
              <span>
                历史查货成功：
                {String(
                  (
                    riskAndHistory.sourcing as
                      | Record<string, unknown>
                      | undefined
                  )?.successfulCount ??
                    sourcingHistory.successCount ??
                    0,
                )}{" "}
                次
              </span>
              <span>
                历史无货：
                {String(
                  (
                    riskAndHistory.sourcing as
                      | Record<string, unknown>
                      | undefined
                  )?.noSupplyCount ??
                    sourcingHistory.failureCount ??
                    0,
                )}{" "}
                次
              </span>
              {riskAndHistory.openCase ? (
                <span>
                  已有开放案件：
                  {String(
                    (riskAndHistory.openCase as Record<string, unknown>)
                      .label ?? "待确认",
                  )}
                </span>
              ) : (
                <span>没有可靠开放案件</span>
              )}
            </div>
          </article>
          <details className="triage-technical-details">
            <summary>原始记录与技术证据</summary>
            <dl>
              <Fact
                label="联系人"
                value={String(inquiry.contactName ?? item.customerCandidate)}
              />
              <Fact
                label="公司"
                value={String(inquiry.companyName ?? "待确认")}
              />
              <Fact
                label="产品"
                value={String(
                  inquiry.productDisplayName ?? item.product ?? "待确认",
                )}
              />
              <Fact
                label="CAS"
                value={String(inquiry.casRaw ?? item.cas ?? "待确认")}
              />
              <Fact
                label="数量"
                value={String(inquiry.quantityRaw ?? item.quantity ?? "待确认")}
              />
              <Fact
                label="原始留言"
                value={String(
                  inquiry.safeCommentExcerpt ??
                    (item.dataOrigin === "REAL_GMAIL"
                      ? "真实 Gmail 正文仅在受控证据视图中只读保存"
                      : "合成询单，原始内容只读"),
                )}
              />
            </dl>
          </details>
          <details
            className="triage-technical-details"
            open={item.dataOrigin === "REAL_GMAIL"}
          >
            <summary>
              {item.dataOrigin === "REAL_GMAIL"
                ? "DeepSeek 邮件智能判断"
                : "影子 AI 辅助分析（默认折叠）"}
            </summary>
            <p>
              {shadowAi.status === "NOT_RUN"
                ? "AI 辅助分析：未运行"
                : `AI 状态：${statusLabel(String(shadowAi.status))}`}
            </p>
            {item.dataOrigin === "REAL_GMAIL" ? (
              <dl>
                <Fact
                  label="邮件性质"
                  value={statusLabel(
                    String(shadowAi.canonicalNature ?? "UNKNOWN"),
                  )}
                />
                <Fact
                  label="建议客户等级"
                  value={String(shadowAi.suggestedCustomerGrade ?? "待确认")}
                />
                <Fact
                  label="建议操作"
                  value={statusLabel(
                    String(shadowAi.suggestedAction ?? "HUMAN_REVIEW"),
                  )}
                />
              </dl>
            ) : null}
            <small>
              {item.dataOrigin === "REAL_GMAIL"
                ? "AI 结果是建议证据；客户匹配、去重与内部业务动作由确定性规则执行，客户和供应商外发保持关闭。"
                : realShadow
                  ? "仅允许真实数据影子模式下的受控分析；结果必须人工审核，不会自动修改业务事实。"
                  : "仅允许合成数据、影子模式和测试证据，不会自动修改业务事实。"}
            </small>
          </details>
        </section>
        <section className="triage-evidence triage-legacy-evidence">
          <article>
            <h2>1. 原始询单</h2>
            <dl>
              <Fact
                label="联系人"
                value={String(inquiry.contactName ?? item.customerCandidate)}
              />
              <Fact
                label="公司"
                value={String(inquiry.companyName ?? "待确认")}
              />
              <Fact
                label="产品"
                value={String(
                  inquiry.productDisplayName ?? item.product ?? "待确认",
                )}
              />
              <Fact
                label="CAS"
                value={String(inquiry.casRaw ?? item.cas ?? "待确认")}
              />
              <Fact
                label="数量"
                value={String(inquiry.quantityRaw ?? item.quantity ?? "待确认")}
              />
              <Fact
                label="原始留言"
                value={String(
                  inquiry.safeCommentExcerpt ??
                    (item.dataOrigin === "REAL_GMAIL"
                      ? "真实 Gmail 正文仅在受控证据视图中只读保存"
                      : "合成询单，原始内容只读"),
                )}
              />
            </dl>
          </article>
          <article>
            <h2>2. 客户与可信度</h2>
            <p className="triage-callout">
              邮箱分类：
              <strong>{CATEGORY_LABELS[emailCategory]}</strong> ·{" "}
              {statusLabel(classification.confidence)}
            </p>
            <small>{classification.evidence.join("；")}</small>
            <p>
              客户候选：{item.customerCandidate} ·
              以下维度由确定性规则和内部历史生成，需人工确认。
            </p>
            <div className="triage-dimensions">
              {dimensions.map((entry, index) => {
                const fact = entry as Record<string, unknown>;
                return (
                  <div key={index}>
                    <strong>{String(fact.name)}</strong>
                    <span>
                      {["LOW", "NORMAL", "MEDIUM", "HIGH", "URGENT"].includes(
                        String(fact.result),
                      )
                        ? statusLabel(String(fact.result))
                        : String(fact.result)}
                    </span>
                    <small>
                      {String(fact.evidence)} ·{" "}
                      {EVIDENCE_SOURCE_LABELS[String(fact.source)] ??
                        String(fact.source)}
                    </small>
                  </div>
                );
              })}
            </div>
            <label>
              人工确认邮箱分类
              <select
                value={emailCategory}
                onChange={(event) =>
                  setEmailCategory(event.target.value as TriageCategory)
                }
              >
                {(Object.keys(CATEGORY_LABELS) as TriageCategory[]).map(
                  (value) => (
                    <option value={value} key={value}>
                      {CATEGORY_LABELS[value]}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label>
              客户等级
              <select
                value={customerTier}
                onChange={(event) => setCustomerTier(event.target.value)}
              >
                {["UNKNOWN", "A", "B", "C", "D", "E"].map((value) => (
                  <option value={value} key={value}>
                    {value === "UNKNOWN" ? "待确认" : `等级 ${value}`}
                  </option>
                ))}
              </select>
            </label>
          </article>
          <article>
            <h2>3. 重复、风险与历史</h2>
            <p>
              风险：{statusLabel(item.risk)} · 重复候选：{candidates.length}
            </p>
            <ul>
              {history.slice(0, 5).map((entry, index) => (
                <li key={index}>
                  {String(
                    (entry as Record<string, unknown>).sourceInquiryId ??
                      "历史询单",
                  )}{" "}
                  ·{" "}
                  {statusLabel(
                    String(
                      (entry as Record<string, unknown>).status ?? "UNKNOWN",
                    ),
                  )}
                </li>
              ))}
            </ul>
            <p>{String(recommendation.reason ?? "规则建议待确认")}</p>
            <div className="triage-recommendation">
              <strong>
                {item.dataOrigin === "REAL_GMAIL"
                  ? "DeepSeek 邮件智能"
                  : "影子 AI"}
              </strong>
              <span>{statusLabel(String(shadowAi.status ?? "NOT_RUN"))}</span>
              <small>
                {Array.isArray(shadowAi.labels)
                  ? shadowAi.labels.join(" · ")
                  : item.dataOrigin === "REAL_GMAIL"
                    ? "REAL_GMAIL · DEEPSEEK · ADVISORY"
                    : "合成数据 · 只读影子 · 仅供测试"}
                ；
                {item.dataOrigin === "REAL_GMAIL"
                  ? "业务动作由确定性策略执行。"
                  : "结果不会自动应用。"}
              </small>
            </div>
          </article>
          <article>
            <h2>4. 查货判断</h2>
            <p>
              {String(
                sourcingHistory.recommendation ??
                  (Boolean(recommendation.requiresSourcing)
                    ? "建议创建普通查货任务"
                    : "建议复用近期有效查货结果或跳过查货"),
              )}
            </p>
            <small>
              一年内成功 {String(sourcingHistory.successCount ?? 0)} 次 · 无货{" "}
              {String(sourcingHistory.failureCount ?? 0)} 次。
              建议依据：历史查货记录、产品风险、规格和数量；最终由分诊人员确认。
            </small>
          </article>
        </section>
        <aside className="triage-action decision-rail">
          <div className="decision-rail-heading">
            <span className="triage-eyebrow">决策操作栏</span>
            <h2>系统建议</h2>
          </div>
          <div className="decision-recommendation-card">
            <span className="decision-badge decision-badge-primary">
              {String(recommendedPackage.confidence ?? "MEDIUM") === "HIGH"
                ? "高置信"
                : String(recommendedPackage.confidence ?? "MEDIUM") === "LOW"
                  ? "需要人工判断"
                  : "中置信"}
            </span>
            <h3>
              {String(
                recommendedPackage.headline ??
                  recommendation.reason ??
                  "等待系统建议",
              )}
            </h3>
            <p>
              {realShadow
                ? realEvidenceSummary(
                    recommendedPackage.summary ?? "请先完成当前证据核对",
                  )
                : String(recommendedPackage.summary ?? "请先完成当前证据核对")}
            </p>
            {recommendedPackage.recommendedSales ? (
              <div className="decision-target">
                <strong>销售</strong>
                <span>
                  {String(
                    (
                      recommendedPackage.recommendedSales as Record<
                        string,
                        unknown
                      >
                    ).label,
                  )}
                </span>
                <small>
                  {String(
                    (
                      recommendedPackage.recommendedSales as Record<
                        string,
                        unknown
                      >
                    ).summary ?? "",
                  )}
                </small>
              </div>
            ) : null}
            {recommendedPackage.recommendedSourcing ? (
              <div className="decision-target">
                <strong>查货</strong>
                <span>
                  {String(
                    (
                      recommendedPackage.recommendedSourcing as Record<
                        string,
                        unknown
                      >
                    ).label,
                  )}
                </span>
                <small>
                  {String(
                    (
                      recommendedPackage.recommendedSourcing as Record<
                        string,
                        unknown
                      >
                    ).summary ?? "",
                  )}
                </small>
              </div>
            ) : null}
            {recommendedPackage.recommendedTarget ? (
              <div className="decision-target">
                <strong>推荐对象</strong>
                <span>
                  {String(
                    (
                      recommendedPackage.recommendedTarget as Record<
                        string,
                        unknown
                      >
                    ).label,
                  )}
                </span>
                <small>
                  {String(
                    (
                      recommendedPackage.recommendedTarget as Record<
                        string,
                        unknown
                      >
                    ).summary ?? "",
                  )}
                </small>
              </div>
            ) : null}
            {recommendedPackage.blockingReasons &&
            Array.isArray(recommendedPackage.blockingReasons) &&
            recommendedPackage.blockingReasons.length ? (
              <div className="decision-blockers">
                <strong>阻塞项</strong>
                {recommendedPackage.blockingReasons.map((entry, index) => (
                  <span key={index}>• {String(entry)}</span>
                ))}
              </div>
            ) : (
              <div className="decision-ok">✓ 当前没有阻塞项</div>
            )}
            {packageEvidence.length ? (
              <details className="decision-evidence-details">
                <summary>查看推荐证据（{packageEvidence.length}）</summary>
                {packageEvidence.map((entry, index) => {
                  const evidence = entry as Record<string, unknown>;
                  return (
                    <span key={index}>
                      • {String(evidence.label)}：{String(evidence.detail)}
                    </span>
                  );
                })}
              </details>
            ) : null}
          </div>
          {!isProcessed ? (
            <details className="triage-other-dispositions">
              <summary>其他处置 / 修改建议</summary>
              <label>
                最终处置
                <select
                  value={disposition}
                  onChange={(event) =>
                    setDisposition(event.target.value as Disposition)
                  }
                >
                  {Object.entries(DISPOSITION_LABELS).map(([value, label]) => (
                    <option value={value} key={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                销售负责人
                <select
                  value={salesUserId}
                  onChange={(event) => setSalesUserId(event.target.value)}
                >
                  <option value="">请选择有效销售成员</option>
                  {salesMembers.map((member) => (
                    <option value={member.userId} key={member.id}>
                      {member.user?.displayName ?? member.userId}
                    </option>
                  ))}
                </select>
              </label>
              {disposition === "PARALLEL_DISTRIBUTE" ? (
                <label>
                  查货负责人
                  <select
                    value={sourcingUserId}
                    onChange={(event) => setSourcingUserId(event.target.value)}
                  >
                    <option value="">请选择有效查货成员</option>
                    {sourcingMembers.map((member) => (
                      <option value={member.userId} key={member.id}>
                        {member.user?.displayName ?? member.userId}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {disposition === "LINK_EXISTING_CASE" ? (
                <label>
                  已有案件 ID
                  <input
                    value={existingCaseId}
                    onChange={(event) => setExistingCaseId(event.target.value)}
                    placeholder="选择或输入当前工作空间的案件 ID"
                  />
                </label>
              ) : null}
              {disposition === "MERGE_DUPLICATE" ? (
                <label>
                  重复询单
                  <select
                    value={otherInquiryId}
                    onChange={(event) => setOtherInquiryId(event.target.value)}
                  >
                    <option value="">请选择重复候选</option>
                    {history.map((entry, index) => {
                      const record = entry as Record<string, unknown>;
                      return (
                        <option value={String(record.id ?? "")} key={index}>
                          {String(record.sourceInquiryId ?? "历史询单")}
                        </option>
                      );
                    })}
                  </select>
                </label>
              ) : null}
              <label>
                处置原因
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              </label>
              <div className="triage-recommendation">
                <strong>系统建议</strong>
                <span>{String(recommendation.reason ?? "请完成证据确认")}</span>
                <small>
                  {item.dataOrigin === "REAL_GMAIL"
                    ? "AI 结果仅作为建议证据；客户匹配、去重和内部业务动作由确定性规则执行，客户与供应商外发保持关闭。"
                    : "AI 结果仅作为影子/模拟证据，不能自动修改业务事实。"}
                </small>
              </div>
            </details>
          ) : null}
          {notice ? (
            <div className="triage-feedback success">{notice}</div>
          ) : null}
          {error ? <div className="triage-feedback error">{error}</div> : null}
          {isProcessed ? (
            <div className="triage-distribution-success">
              <strong>已处理</strong>
              <span>
                案件：
                {String(
                  processingResult.caseNumber ??
                    processingResult.caseId ??
                    "已创建",
                )}
              </span>
              <span>
                内部分配：
                {processingResult.salesAssignmentId
                  ? "销售已分配"
                  : "销售待复核"}
                {processingResult.sourcingAssignmentId ? " · 查货已分配" : ""}
              </span>
              <span>
                任务：
                {Array.isArray(processingResult.taskIds)
                  ? processingResult.taskIds.length
                  : 0}
                个
              </span>
              <div className="triage-inline-actions">
                <button
                  type="button"
                  onClick={() => {
                    const caseId = String(processingResult.caseId ?? "");
                    if (caseId)
                      window.location.assign(
                        `/?view=inquiry-center&case=${encodeURIComponent(caseId)}`,
                      );
                  }}
                >
                  打开案件生命周期工作台
                </button>
                <button type="button" onClick={onNext}>
                  处理下一条询单
                </button>
              </div>
            </div>
          ) : distributionResult && !realShadow ? (
            <div className="triage-distribution-success">
              <strong>分发成功</strong>
              <span>
                案件：
                {String(
                  distributionResult.caseNumber ??
                    distributionResult.caseId ??
                    "已创建",
                )}
              </span>
              <span>销售任务：已创建并进入负责人队列</span>
              <span>
                查货任务：
                {distributionResult.sourcingAssignmentId
                  ? "已创建并进入负责人队列"
                  : "按策略跳过"}
              </span>
              <div className="triage-inline-actions">
                <button
                  type="button"
                  onClick={() => {
                    const caseId = String(distributionResult.caseId ?? "");
                    if (caseId)
                      window.location.assign(
                        `/?view=inquiry-center&case=${encodeURIComponent(caseId)}`,
                      );
                  }}
                >
                  打开案件生命周期工作台
                </button>
                <button type="button" onClick={onNext}>
                  处理下一条询单
                </button>
              </div>
            </div>
          ) : realShadow ? (
            <button
              type="button"
              className="triage-primary-action"
              disabled={busy || !reason.trim()}
              onClick={() => onSubmit()}
            >
              {busy
                ? "记录中…"
                : `记录人工审核：${String(recommendedPackage.headline ?? "影子建议")}`}
            </button>
          ) : (
            <button
              type="button"
              className="triage-primary-action"
              disabled={
                busy ||
                !reason.trim() ||
                (String(recommendedPackage.confidence ?? "MEDIUM") === "LOW" &&
                  disposition ===
                    String(recommendedPackage.executionDisposition ?? "")) ||
                (["PARALLEL_DISTRIBUTE", "SALES_ONLY"].includes(disposition) &&
                  (emailCategory === "UNKNOWN" ||
                    customerTier === "UNKNOWN")) ||
                (disposition === "LINK_EXISTING_CASE" && !existingCaseId) ||
                (disposition === "MERGE_DUPLICATE" && !otherInquiryId)
              }
              onClick={() => onSubmit()}
            >
              {busy
                ? "提交中…"
                : String(recommendedPackage.confidence ?? "MEDIUM") === "LOW" &&
                    disposition ===
                      String(recommendedPackage.executionDisposition ?? "")
                  ? "需要人工判断后执行"
                  : `按建议执行：${String(recommendedPackage.headline ?? DISPOSITION_LABELS[disposition])}`}
            </button>
          )}
          <small>
            提交前请确认邮箱分类、客户等级、重复、风险、查货策略和负责人。
          </small>
        </aside>
      </div>
    </main>
  );
}

function isFormalInquiryDistributionResult(
  value: unknown,
): value is FormalInquiryDistributionResult {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<FormalInquiryDistributionResult>;
  return (
    (candidate.outcome === "PROCESSED" ||
      candidate.outcome === "ALREADY_PROCESSED") &&
    typeof candidate.inquiryId === "string" &&
    typeof candidate.caseId === "string" &&
    typeof candidate.caseNumber === "string" &&
    Array.isArray(candidate.taskIds) &&
    Array.isArray(candidate.notifications)
  );
}

function triageDecisionLabel(status: string, businessState?: string): string {
  if (businessState === "AI_UNAVAILABLE") return "AI 分析暂未完成";
  // "正常" is the same species of no-op conclusion as "当前未发现已确认风险": an empty 建议处置
  // cell already reads as "nothing to do", so the queue does not spend a line saying it.
  if (status === "PASS") return "";
  if (status === "WARNING") return "需注意";
  if (status === "PENDING") return "需复核";
  if (status === "REJECT") return "不建议承接";
  return "分析中";
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
